import React, { useCallback, useContext, useEffect, useState, useMemo, useDeferredValue, startTransition, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import PricingBreakDown from "./PricingBreakDown";
import ItemDetails from "./ItemDetails";
import { sanitizePromoStoreDescriptionHtml } from "./PromoStoreDescriptionEditor/sanitizeHtml";
import Decoration from "./Decoration";
import { isFixedChargeRow, savedDecorationQuantity } from "./Decoration/matrixChargeType";
import PricingSummary from "./PricingSummary";
import PromoStoreStorefrontPricingRules, {
  normalizeStorefrontPricingRules,
} from "./PromoStoreStorefrontPricingRules";
import { getTaxes } from "../../services/tax";
import { parseOrderProductId, CUSTOM_PREFIX } from "../../services/products";
import { getOrderItem, updateOrderItems } from "../../services/orderItem";
import ButtonLoader from '../ButtonLoader'
import { getCustomerFiles } from "../../services/customerFiles";
import {
  buildSavedDecorationSourceOptions,
  buildSupplierDecorationSourceOptions,
  mapDecorationsFromApi,
} from "./index.controller.js";
import LoadingComponent from '../ComponentLoader'
import { uploadFile } from '../../services/customerFiles'
import useEnsureCustomerFilePreviews from "./useEnsureCustomerFilePreviews";
import {
  getArtworkCustomerFileId,
  getArtworkPreviewUrl,
  normalizeArtworkFileEntry,
  normalizeArtworkOriginalUrl,
  normalizeCustomerFileEntry,
  resolveArtworkCustomerFile,
} from "./artworkFileUtils";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { computeOrderItemPricingSnapshot } from "../../utils/pricingEngine";
import { buildResolvedTaxBreakdown, resolveProductTaxes } from "../../utils/totals";
import {
  DISPLAY_STATUS,
  DISPLAY_STATUS_OPTIONS,
  normalizeDisplayStatus,
} from "../../utils/pricingOptions";
import {
  buildProductTaxPatch,
  buildPromoStoreTaxState,
  findTaxOptionById,
  normalizeRateDecimal,
  normalizeTaxIds,
} from "./taxSelection";
import { normalizePromoStoreImageSelection } from "./asiImageUtils";
import { resolveSupplierCurrencyCode } from "./supplierCurrency";
import { isPromoStoreProductDecorationEnabled } from "../../utils/promoStoreProductDecorationCapability";
import {
  assignSequentialPricingSortOrder,
  resolvePersistedPricingSortOrder,
  sortPricingRowsForHydration,
} from "./pricingRowOrder";
import pricingMath from "./pricingMath";
import { shouldFetchOrderItemForEditProduct } from "./editHydration";
import { AuthContext } from "../../helpers/AuthContext";
import { normalizeInvoiceRoles } from "../../utils/invoicePermissions";

// ──────────────────────────────────────────────
// LOCK HELPERS (add in EditProduct.jsx once)
// ──────────────────────────────────────────────

// what can still change while locked:
const LOCK_ALLOWED_KEYS = new Set(["netCost", "sku", "inventoryQuantity"]);
const isLockedBool = (value) => value === true || String(value).toLowerCase() === "true";
const { calculateMarginFromSellPrice } = pricingMath;

// Manual/automatic pricing mode is a Super Administrator-only operation.
// Keep it independent from the bulk Retail header lock used by sales reps.
const canUseManualPricingMode = (authState = {}) =>
  normalizeInvoiceRoles(authState).has("super-administrator");

const canShowRetailBulkLock = ({ authState = {}, source, isPromoStoreProduct = false, controlledMode = false } = {}) => {
  // Controlled Order Cart edits belong to the separate Showcase flow.
  if (isPromoStoreProduct || (source === "order-cart" && controlledMode)) return false;
  const roles = normalizeInvoiceRoles(authState);
  if (!["order-cart", "sales-order-cart", "invoicing-cart"].includes(source)) return false;
  if (roles.has("administrator") || roles.has("finance-rep") || roles.has("purchasing-rep")) return true;
  // Preserve the existing invoicing-cart visibility rules for sales reps.
  return source !== "invoicing-cart" && (roles.has("client-rep") || roles.has("order-rep"));
};

// Retail header uses its own persisted bit. Never rewrite legacy IsLocked
// (the Net Cost header/decoration lock) when locking or unlocking Retail.
const toggleRetailPricingRowLocks = (rows = []) => {
  if (!Array.isArray(rows) || !rows.length) return rows;
  const nextLocked = !rows.every((row) => isLockedBool(row?.isRetailLocked));
  return rows.map((row) => ({ ...row, isRetailLocked: nextLocked }));
};

const shouldTriggerDialogSaveOnEnter = (event) => {
  if (event.key !== "Enter") return false;
  if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return false;
  if (event.defaultPrevented || event.nativeEvent?.isComposing) return false;

  const target = event.target;
  if (!target) return true;

  const tagName = String(target.tagName || "").toUpperCase();
  if (tagName === "TEXTAREA") return false;

  if (target.isContentEditable) return false;
  if (target.getAttribute?.("aria-multiline") === "true") return false;
  if (target.closest?.('[role="textbox"][aria-multiline="true"]')) return false;

  return true;
};

const freezeLockedRow = (
  prevRow,
  patch,
  policy = COUPLED_RETAIL_PRICING_POLICY,
  respectPricingLocks = true
) => {
  const next = { ...prevRow };

  if (!respectPricingLocks || !isPricingRetailLocked(prevRow, policy)) return { ...next, ...patch };

  // Existing IsLocked must keep its original whole-row behavior, including
  // frozen Quantity and Margin (but editable Net Cost/SKU).
  if (!isLockedBool(prevRow?.isLocked) && !usesIndependentRetail(policy)) {
    // The new Retail-only lock protects the sell price, not other fields.
    for (const [k, v] of Object.entries(patch || {})) {
      if (!["retail", "clientPrice"].includes(k)) next[k] = v;
    }
    next.retail = prevRow.retail;
    next.clientPrice = prevRow.clientPrice;
    return next;
  }

  for (const [k, v] of Object.entries(patch || {})) {
    if (LOCK_ALLOWED_KEYS.has(k)) next[k] = v;
  }

  next.retail      = prevRow.retail;
  next.clientPrice = prevRow.clientPrice;
  next.margin      = prevRow.margin;
  next.quantity = prevRow.quantity;

  // total can change with qty, but PromoStore locked rows still total off client price
  const q = Number(next.quantity || 0);
  const lockedSellPrice = usesIndependentRetail(policy)
    ? Number(prevRow.clientPrice ?? prevRow.retail ?? 0)
    : Number(prevRow.retail || 0);
  next.total = +(lockedSellPrice * q).toFixed(2);
  return next;
};

const INVENTORY_TRACKING = {
  track: "Track inventory",
  noTrack: "Do not track inventory",
};

const normalizeInventoryTracking = (value) => {
  if (value === INVENTORY_TRACKING.noTrack) return INVENTORY_TRACKING.noTrack;
  if (value === INVENTORY_TRACKING.track) return INVENTORY_TRACKING.track;

  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "do not track inventory" || normalized === "do not track") {
    return INVENTORY_TRACKING.noTrack;
  }
  if (normalized === "track inventory" || normalized === "track") {
    return INVENTORY_TRACKING.track;
  }
  return INVENTORY_TRACKING.track;
};

const getDecorationId = (decoration = {}) =>
  decoration.id ?? decoration.Id ?? decoration.DecorationId ?? decoration.decorationId ?? null;

const getArtworkDecorationId = (artwork = {}) =>
  artwork.DecorationId ?? artwork.decorationId ?? artwork.PromoStoreItemDecorationId ?? null;

const attachArtworksToDecorations = (decorations = [], artworks = []) => {
  const decorationList = Array.isArray(decorations) ? decorations : [];
  const artworkList = Array.isArray(artworks) ? artworks : [];

  return decorationList.map((decoration) => {
    const decorationId = getDecorationId(decoration);
    const matchedArtworks = artworkList.filter((artwork) => {
      const artworkDecorationId = getArtworkDecorationId(artwork);
      return decorationId != null && artworkDecorationId != null
        ? String(artworkDecorationId) === String(decorationId)
        : false;
    }).map((matchedArtwork) => ({
      id: matchedArtwork.id ?? null,
      ImprintType: matchedArtwork.ImprintType ?? matchedArtwork.imprintType ?? "",
      Image: matchedArtwork.Image ?? matchedArtwork.selectedImage ?? matchedArtwork.filePath ?? matchedArtwork.url ?? null,
      PreviewUrl:
        matchedArtwork.PreviewUrl ??
        matchedArtwork.previewUrl ??
        matchedArtwork.previewImageUrl ??
        matchedArtwork.PreviewImageUrl ??
        null,
      CustomerFileId: matchedArtwork.CustomerFileId ?? matchedArtwork.customerFileId ?? null,
      ProofRequired: matchedArtwork.ProofRequired ?? matchedArtwork.proofRequired ?? "",
      ProofRequiredAction: matchedArtwork.ProofRequiredAction ?? matchedArtwork.proofRequiredAction ?? null,
      LogoName: matchedArtwork.LogoName ?? matchedArtwork.logoName ?? "",
      LogoSize: matchedArtwork.LogoSize ?? matchedArtwork.logoSize ?? "",
      LogoColor: matchedArtwork.LogoColor ?? matchedArtwork.logoColor ?? "",
      RepeatLogo: matchedArtwork.RepeatLogo ?? matchedArtwork.repeatLogo ?? "",
      SupplierNotes: matchedArtwork.SupplierNotes ?? matchedArtwork.supplierNotes ?? "",
      Charge: matchedArtwork.Charge ?? matchedArtwork.charge ?? 0,
    }));

    const existingArtWorks = Array.isArray(decoration.ArtWorks)
      ? decoration.ArtWorks
      : Array.isArray(decoration.artWorks)
        ? decoration.artWorks
        : [];
    // Some older Sales Order carts hold a blank nested artwork placeholder,
    // while the actual saved artwork lives on the product's artworks array.
    // A blank placeholder must not hide a matching persisted artwork.
    const hasArtworkContent = (artwork) => Boolean(
      artwork && (
        artwork.Image || artwork.image || artwork.selectedImage ||
        artwork.PreviewUrl || artwork.previewUrl ||
        artwork.CustomerFileId || artwork.customerFileId ||
        artwork.LogoName || artwork.logoName ||
        artwork.ImprintType || artwork.imprintType
      )
    );
    const populatedEmbedded = existingArtWorks.filter(hasArtworkContent);
    const artWorks = populatedEmbedded.length
      ? populatedEmbedded
      : matchedArtworks.length
        ? matchedArtworks
        : existingArtWorks;
    const existingFirstArtwork = decoration.ArtWork;
    const firstArtwork = hasArtworkContent(existingFirstArtwork)
      ? existingFirstArtwork
      : artWorks.find(hasArtworkContent) || existingFirstArtwork || artWorks[0] || null;
    if (!firstArtwork && !artWorks.length) return decoration;

    return {
      ...decoration,
      ArtWorks: artWorks,
      ArtWork: firstArtwork,
    };
  });
};


// consider any still-being-typed decimal as transient
const isTransientDecimal = (v) => {
  if (typeof v !== "string") return false;
  const s = v;
  return (
    // "2.0", "2.00" (only zeros after dot so far)
    // "2."
    (s === ""            // empty
                                                                                                                                                                                                                                          || s === "."        // just a dot
                                                                                                                                                                                                                                          || /^\d+\.$/.test(s) || /^\d+\.0+$/.test(s))
  );
};

const patchHasOwn = (patch, key) =>
  Object.prototype.hasOwnProperty.call(patch || {}, key);

const COUPLED_RETAIL_PRICING_POLICY = Object.freeze({
  retailMode: "coupled",
  allowManualPricing: true,
});

const PROMOSTORE_RETAIL_PRICING_POLICY = Object.freeze({
  retailMode: "independent",
  allowManualPricing: false,
});

const resolvePricingPolicy = ({ isPromoStore = false } = {}) =>
  isPromoStore ? PROMOSTORE_RETAIL_PRICING_POLICY : COUPLED_RETAIL_PRICING_POLICY;

const usesIndependentRetail = (policy = COUPLED_RETAIL_PRICING_POLICY) =>
  policy?.retailMode === "independent";

const isPricingRetailLocked = (row = {}, policy = COUPLED_RETAIL_PRICING_POLICY) =>
  isLockedBool(row?.isLocked) ||
  (!usesIndependentRetail(policy) && isLockedBool(row?.isRetailLocked ?? row?.IsRetailLocked));

const PRICING_EDIT_INTENT = Object.freeze({
  RETAIL_ONLY: "retail_only",
  QUANTITY_ONLY: "quantity_only",
});

const recomputeRetailDrivenRow = (row, effectiveCost) => {
  const q = Number(row.quantity || 0);
  const retail = Number(row.retail || 0);
  const margin = calculateMarginFromSellPrice(retail, effectiveCost, row.margin);
  return {
    ...row,
    retail,
    clientPrice: retail,
    total: +(retail * q).toFixed(2),
    margin,
    Margin: margin,
  };
};

const recomputeIndependentRetailRow = (row) => ({
  ...row,
  retail: Number(row.retail || 0),
  clientPrice: Number(row.clientPrice || 0),
  total: Number(row.total || 0),
  margin: Number(row.margin || 0),
  Margin: Number(row.Margin ?? row.margin ?? 0),
});

const recomputeIndependentQuantityRow = (row) => {
  const quantity = Number(row.quantity || 0);
  const clientPrice = Number(row.clientPrice || 0);
  const margin = Number(row.margin || 0);
  return {
    ...row,
    quantity,
    retail: Number(row.retail || 0),
    clientPrice,
    total: +(clientPrice * quantity).toFixed(2),
    margin,
    Margin: Number(row.Margin ?? margin),
  };
};

// MANUAL MODE: retail stays authoritative, margin is informative, quantity still drives total.
const recomputeManualRow = (row, patch = {}) => {
  if (patchHasOwn(patch, "retail")) {
    return recomputeRetailDrivenRow(row, Number(row.netCost || 0));
  }

  const q = Number(row.quantity || 0);
  const r = Number(row.retail || 0);
  return {
    ...row,
    clientPrice: r,
    total: +(r * q).toFixed(2),
  };
};



// normal recompute for UNLOCKED rows only
const recomputeUnlockedRow = (row, defaultMargin = 40) => {
  const q  = Number(row.quantity || 0);
  const nc = Number(row.netCost  || 0);
  const m  = Number(row.margin   ?? defaultMargin);

  const retail = m < 100 ? +(nc / (1 - m/100)).toFixed(2) : Number(row.retail || 0);
  return {
    ...row,
    retail,
    clientPrice: retail,
    total: +(retail * q).toFixed(2),
  };
};

const recomputePromoStoreUnlockedRow = (row, defaultMargin = 40) => {
  const q = Number(row.quantity || 0);
  const nc = Number(row.netCost || 0);
  const m = Number(row.margin ?? defaultMargin);
  const clientPrice = m < 100 ? +(nc / (1 - m / 100)).toFixed(2) : Number(row.clientPrice || 0);
  const retail = Number(row.retail ?? row.clientPrice ?? 0);
  const margin = calculateMarginFromSellPrice(clientPrice, nc, row.margin);

  return {
    ...row,
    retail,
    clientPrice,
    total: +(clientPrice * q).toFixed(2),
    margin,
    Margin: margin,
  };
};

const recomputeUnlockedRowFromRetail = (row) =>
  recomputeRetailDrivenRow(row, Number(row.netCost || 0));


const hasConvertedCostBasis = (row = {}) => {
  const converted = Number(row.convertedNetCost ?? row.ConvertedNetCost);
  const rate = Number(row.exchangeRate ?? row.ExchangeRate);
  return Number.isFinite(converted) && converted > 0 && Number.isFinite(rate) && rate > 0;
};

// Retail-only locks must keep the currency basis even when a 100% margin
// temporarily makes ConvertedNetCost zero.
const hasRetailLockedConversionBasis = (row = {}) => {
  const converted = row.convertedNetCost ?? row.ConvertedNetCost;
  const rate = Number(row.exchangeRate ?? row.ExchangeRate);
  return hasConvertedCostBasis(row) ||
    (converted != null && converted !== "" && Number(converted) === 0 && Number.isFinite(rate) && rate > 0);
};

const recomputeConvertedCostBasisRow = (row, patch = {}) => {
  const q = Number(row.quantity || 0);
  const rate = Number(row.exchangeRate ?? row.ExchangeRate);
  const netCost = Number(row.netCost || 0);
  const nextConvertedNetCost = Number.isFinite(rate) && rate > 0
    ? +(netCost * rate).toFixed(2)
    : Number(row.convertedNetCost ?? row.ConvertedNetCost ?? 0);

  const retail = Number(row.retail || 0);
  const retailWasManuallyChanged = patchHasOwn(patch, "retail");
  const clientPriceWasManuallyChanged = patchHasOwn(patch, "clientPrice");
  const clientPrice = clientPriceWasManuallyChanged || retailWasManuallyChanged
    ? Number((clientPriceWasManuallyChanged ? row.clientPrice : retail) || 0)
    : Number(row.clientPrice ?? retail ?? 0);
  const sellPrice = clientPrice || retail;
  const margin = calculateMarginFromSellPrice(sellPrice, nextConvertedNetCost, row.margin);

  return {
    ...row,
    netCost,
    convertedNetCost: nextConvertedNetCost,
    ConvertedNetCost: nextConvertedNetCost,
    exchangeRate: rate,
    ExchangeRate: rate,
    retail,
    clientPrice,
    total: +(clientPrice * q).toFixed(2),
    margin,
    Margin: margin,
  };
};

const recomputePromoStoreConvertedCostBasisRow = (row, defaultMargin = 40) => {
  const q = Number(row.quantity || 0);
  const rate = Number(row.exchangeRate ?? row.ExchangeRate);
  const netCost = Number(row.netCost || 0);
  const nextConvertedNetCost = Number.isFinite(rate) && rate > 0
    ? +(netCost * rate).toFixed(2)
    : Number(row.convertedNetCost ?? row.ConvertedNetCost ?? 0);
  const marginSeed = Number(row.margin ?? defaultMargin);
  const clientPrice = marginSeed < 100
    ? +(nextConvertedNetCost / (1 - (marginSeed / 100))).toFixed(2)
    : Number(row.clientPrice || 0);
  const retail = Number(row.retail ?? row.clientPrice ?? 0);
  const margin = calculateMarginFromSellPrice(clientPrice, nextConvertedNetCost, row.margin);

  return {
    ...row,
    netCost,
    convertedNetCost: nextConvertedNetCost,
    ConvertedNetCost: nextConvertedNetCost,
    exchangeRate: rate,
    ExchangeRate: rate,
    retail,
    clientPrice,
    total: +(clientPrice * q).toFixed(2),
    margin,
    Margin: margin,
  };
};

const recomputeEditableRow = (
  row,
  defaultMargin = 40,
  policy = COUPLED_RETAIL_PRICING_POLICY
) => {
  if (hasConvertedCostBasis(row)) {
    return usesIndependentRetail(policy)
      ? recomputePromoStoreConvertedCostBasisRow(row, defaultMargin)
      : recomputeConvertedCostBasisRow(row);
  }

  if (usesIndependentRetail(policy)) {
    return recomputePromoStoreUnlockedRow(row, defaultMargin);
  }

  return row.isManual
    ? recomputeManualRow(row)
    : recomputeUnlockedRow(row, defaultMargin);
};

const shouldPreservePromoStoreRetailEdit = (row, policy = COUPLED_RETAIL_PRICING_POLICY) =>
  usesIndependentRetail(policy) && (
    row?.__pricingEditIntent === PRICING_EDIT_INTENT.RETAIL_ONLY ||
    row?.__pricingEditIntent === PRICING_EDIT_INTENT.QUANTITY_ONLY
  );

const finalizePromoStoreRetailEditRow = (row = {}, defaultMargin = 40) => {
  const cleanMargin = row.margin === "" ? defaultMargin : Number(row.margin ?? defaultMargin);
  const cleanRetail = row.retail === "" ? 0 : Number(row.retail || 0);
  const cleanClientPrice = row.clientPrice === "" ? 0 : Number(row.clientPrice || 0);
  const cleanTotal = row.total === "" ? 0 : Number(row.total || 0);
  const cleanQuantity = row.quantity === "" ? 0 : Number(row.quantity || 0);
  const cleanNetCost = row.netCost === "" ? 0 : Number(row.netCost || 0);
  const { __pricingEditIntent, ...rest } = row;

  const finalized = {
    ...rest,
    netCost: cleanNetCost,
    quantity: cleanQuantity,
    retail: cleanRetail,
    clientPrice: cleanClientPrice,
    total: cleanTotal,
    margin: cleanMargin,
    Margin: Number(row.Margin ?? cleanMargin),
    convertedNetCost: row.convertedNetCost ?? row.ConvertedNetCost ?? null,
    exchangeRate: row.exchangeRate ?? row.ExchangeRate ?? null,
  };
  return row?.__pricingEditIntent === PRICING_EDIT_INTENT.QUANTITY_ONLY
    ? {
        ...finalized,
        total: +(cleanClientPrice * cleanQuantity).toFixed(2),
      }
    : finalized;
};

const normalizePricingRowsForEditState = (
  nextRows = [],
  prevRows = [],
  {
    defaultMargin = 40,
    pricingPolicy = COUPLED_RETAIL_PRICING_POLICY,
    respectPricingLocks = true,
  } = {}
) =>
  (nextRows || []).map((row, i) => {
    const before = prevRows[i] || {};

    if (respectPricingLocks && isPricingRetailLocked(row, pricingPolicy)) {
      const frozenQty = Number((isLockedBool(row?.isLocked) ? before.quantity : row.quantity) ?? 0);
      const maybeNewNetCost = isTransientDecimal(row.netCost)
        ? row.netCost
        : Number((row.netCost ?? before.netCost) ?? 0);
      const frozenRetail = Number(before.retail ?? row.retail ?? 0);
      const frozenClientPrice = Number(before.clientPrice ?? row.clientPrice ?? frozenRetail);
      const frozenMargin = isLockedBool(row?.isLocked)
        ? (hasConvertedCostBasis(row)
            ? Number(row.margin ?? before.margin ?? defaultMargin)
            : Number(before.margin ?? row.margin ?? defaultMargin))
        : isTransientDecimal(row.margin)
          ? row.margin
          : !isTransientDecimal(maybeNewNetCost)
            ? calculateMarginFromSellPrice(
                frozenRetail,
                hasRetailLockedConversionBasis(row)
                  ? +(Number(maybeNewNetCost || 0) * Number(row.exchangeRate ?? row.ExchangeRate)).toFixed(2)
                  : maybeNewNetCost,
                row.margin ?? defaultMargin
              )
            : Number(row.margin ?? before.margin ?? defaultMargin);

      const frozenConvertedNetCost =
        row.convertedNetCost ?? row.ConvertedNetCost ?? before.convertedNetCost ?? before.ConvertedNetCost ?? null;
      const frozenExchangeRate =
        row.exchangeRate ?? row.ExchangeRate ?? before.exchangeRate ?? before.ExchangeRate ?? null;

      return {
        ...row,
        quantity: frozenQty,
        netCost: maybeNewNetCost,
        convertedNetCost: frozenConvertedNetCost,
        ConvertedNetCost: frozenConvertedNetCost,
        exchangeRate: frozenExchangeRate,
        ExchangeRate: frozenExchangeRate,
        margin: frozenMargin,
        ...(!usesIndependentRetail(pricingPolicy) ? { Margin: frozenMargin } : {}),
        retail: frozenRetail,
        clientPrice: frozenClientPrice,
        total: +(
          (usesIndependentRetail(pricingPolicy) ? frozenClientPrice : frozenRetail) * frozenQty
        ).toFixed(2),
      };
    }

    const hasTransient =
      isTransientDecimal(row.netCost) ||
      isTransientDecimal(row.margin) ||
      isTransientDecimal(row.retail) ||
      isTransientDecimal(row.clientPrice) ||
      isTransientDecimal(row.total);

    if (hasTransient) return row;

    if (shouldPreservePromoStoreRetailEdit(row, pricingPolicy)) {
      return finalizePromoStoreRetailEditRow(row, defaultMargin);
    }

    const coerced = {
      ...row,
      quantity: Number(row.quantity ?? 0),
      netCost: Number(row.netCost ?? 0),
      retail: Number(row.retail ?? 0),
      margin: Number(row.margin ?? defaultMargin),
      convertedNetCost: row.convertedNetCost ?? row.ConvertedNetCost ?? null,
      exchangeRate: row.exchangeRate ?? row.ExchangeRate ?? null,
    };

    return recomputeEditableRow(coerced, defaultMargin, pricingPolicy);
  });

// unified patcher you’ll call from the UI
const applyRowPatch = (
  row,
  patch,
  defaultMargin = 40,
  policy = COUPLED_RETAIL_PRICING_POLICY,
  respectPricingLocks = true
) => {
  const base = freezeLockedRow(row, patch, policy, respectPricingLocks);
  if (respectPricingLocks && isPricingRetailLocked(base, policy)) {
    const retailOnlyLocked = !isLockedBool(base.isLocked) && !usesIndependentRetail(policy);
    if (retailOnlyLocked && patchHasOwn(patch, "margin")) {
      // The selling price is fixed: changing Margin adjusts Net Cost instead.
      // Keep partial decimal input intact until the field is committed.
      if (isTransientDecimal(base.margin)) {
        const { __transientDecimal, ...clean } = base;
        return clean;
      }
      const margin = Number(base.margin);
      const retail = Number(base.retail || 0);
      if (Number.isFinite(margin) && margin <= 100 && retail > 0) {
        const hasConversion = hasRetailLockedConversionBasis(base);
        const exchangeRate = Number(base.exchangeRate ?? base.ExchangeRate);
        const netCost = +(retail * (1 - margin / 100) / (hasConversion ? exchangeRate : 1)).toFixed(4);
        const convertedCost = hasConversion ? +(netCost * exchangeRate).toFixed(2) : null;
        return {
          ...base,
          netCost,
          ...(hasConversion ? { convertedNetCost: convertedCost, ConvertedNetCost: convertedCost } : {}),
          margin,
          Margin: margin,
          total: +(retail * Number(base.quantity || 0)).toFixed(2),
        };
      }
    }
    if ((hasConvertedCostBasis(base) || (retailOnlyLocked && hasRetailLockedConversionBasis(base))) &&
        !isTransientDecimal(base.netCost)) {
      const normalizedLocked = {
        ...base,
        netCost: base.netCost === "" ? 0 : Number(base.netCost || 0),
        margin: base.margin === "" ? defaultMargin : Number(base.margin ?? defaultMargin),
        retail: base.retail === "" ? 0 : Number(base.retail || 0),
        clientPrice: base.clientPrice === "" ? 0 : Number(base.clientPrice || 0),
        total: base.total === "" ? 0 : Number(base.total || 0),
        quantity: base.quantity === "" ? 0 : Number(base.quantity || 0),
      };
      const { __transientDecimal, ...clean } = recomputeConvertedCostBasisRow(normalizedLocked, patch);
      return clean;
    }
    const { __transientDecimal, ...clean } = base;
    // Only the new Retail-only lock derives Margin from editable Net Cost.
    // Legacy IsLocked and PromoStore retain their existing row behavior.
    if (!isLockedBool(base?.isLocked) && !usesIndependentRetail(policy) && !isTransientDecimal(base.netCost)) {
      const margin = calculateMarginFromSellPrice(base.retail, base.netCost, base.margin);
      return { ...clean, margin, Margin: margin, total: +(Number(base.retail || 0) * Number(base.quantity || 0)).toFixed(2) };
    }
    return clean;
  }

  const transient =
    isTransientDecimal(base.netCost) ||
    isTransientDecimal(base.margin)  ||
    isTransientDecimal(base.retail)  ||
    isTransientDecimal(base.clientPrice) ||
    isTransientDecimal(base.total);

  if (transient || patch?.__transientDecimal) {
    const { __transientDecimal, ...clean } = base;
    return clean;
  }

  const normalized = {
    ...base,
    netCost:     base.netCost     === "" ? 0 : Number(base.netCost     || 0),
    margin:      base.margin      === "" ? defaultMargin : Number(base.margin      ?? defaultMargin),
    retail:      base.retail      === "" ? 0 : Number(base.retail      || 0),
    clientPrice: base.clientPrice === "" ? 0 : Number(base.clientPrice || 0),
    total:       base.total       === "" ? 0 : Number(base.total       || 0),
    quantity:    base.quantity    === "" ? 0 : Number(base.quantity    || 0),
    convertedNetCost: base.convertedNetCost ?? base.ConvertedNetCost ?? null,
    exchangeRate: base.exchangeRate ?? base.ExchangeRate ?? null,
  };

  if (hasConvertedCostBasis(normalized)) {
    if (usesIndependentRetail(policy) && patchHasOwn(patch, "retail")) {
      return recomputeIndependentRetailRow(normalized);
    }
    return usesIndependentRetail(policy)
      ? recomputePromoStoreConvertedCostBasisRow(normalized, defaultMargin)
      : recomputeConvertedCostBasisRow(normalized, patch);
  }

  if (!usesIndependentRetail(policy) && normalized.isManual) return recomputeManualRow(normalized, patch);

  if (patchHasOwn(patch, "margin")) {
    return usesIndependentRetail(policy)
      ? recomputePromoStoreUnlockedRow(normalized, defaultMargin)
      : recomputeUnlockedRow(normalized, defaultMargin);
  }

  if (patchHasOwn(patch, "retail")) {
    return usesIndependentRetail(policy)
      ? recomputeIndependentRetailRow(normalized)
      : recomputeUnlockedRowFromRetail(normalized);
  }

  if (usesIndependentRetail(policy) && patchHasOwn(patch, "quantity") && !patchHasOwn(patch, "netCost") && !patchHasOwn(patch, "margin")) {
    return recomputeIndependentQuantityRow(normalized);
  }

  // auto mode: retail from cost + margin
  return usesIndependentRetail(policy)
    ? recomputePromoStoreUnlockedRow(normalized, defaultMargin)
    : recomputeUnlockedRow(normalized, defaultMargin);
};

const buildEditablePricingRow = (
  pricing = {},
  {
    inventoryMode = false,
    defaultMargin = 40,
    pricingPolicy = COUPLED_RETAIL_PRICING_POLICY,
    asiProductId = "",
  } = {}
) => {
  const locked = isLockedBool(pricing?.IsLocked ?? pricing?.isLocked);
  const retailLocked = isLockedBool(pricing?.IsRetailLocked ?? pricing?.isRetailLocked);
  const quantity = pricing?.Quantity || 0;
  const hasRetail = pricing?.Retail !== undefined && pricing?.Retail !== null && pricing?.Retail !== "";
  const hasClientPrice =
    pricing?.ClientPrice !== undefined && pricing?.ClientPrice !== null && pricing?.ClientPrice !== "";
  const resolvedMargin = Number.isFinite(Number(pricing?.Margin))
    ? Number(pricing.Margin)
    : defaultMargin;
  const expectedRetail =
    resolvedMargin < 100
      ? +((Number(pricing?.NetCost || 0) / (1 - (resolvedMargin / 100))).toFixed(2))
      : 0;
  const persistedClientPrice = hasClientPrice
    ? Number(pricing?.ClientPrice || 0)
    : expectedRetail;
  const retail = usesIndependentRetail(pricingPolicy)
    ? (hasRetail ? Number(pricing?.Retail || 0) : persistedClientPrice)
    : (locked || retailLocked || hasRetail ? Number(pricing?.Retail || 0) : expectedRetail);
  const clientPrice = usesIndependentRetail(pricingPolicy)
    ? persistedClientPrice
    : retail;
  const total = pricing?.Total != null && pricing?.Total !== ""
    ? Number(pricing.Total || 0)
    : +((clientPrice || 0) * (quantity || 0)).toFixed(2);

  return {
    id: getPersistedPricingId(pricing?.id),
    tempRowId: pricing?.tempRowId || pricing?.TempRowId || null,
    sortOrder: resolvePersistedPricingSortOrder(pricing?.sortOrder ?? pricing?.SortOrder),
    type: pricing?.Type || "",
    priceType: resolvePriceTypeFromPricing(pricing),
    chargeName: pricing?.CostName || "",
    asiId: pricing?.ProductId || asiProductId,
    sku: pricing?.SKU || pricing?.sku || "",
    quantity,
    inventoryQuantity:
      pricing?.InventoryQuantity === null || pricing?.InventoryQuantity === undefined
        ? null
        : Number(pricing.InventoryQuantity),
    netCost: pricing?.NetCost || 0,
    convertedNetCost: pricing?.ConvertedNetCost ?? null,
    exchangeRate: pricing?.ExchangeRate ?? null,
    margin: resolvedMargin,
    retail,
    clientPrice,
    total,
    inventoryTracking: inventoryMode ? normalizeInventoryTracking(pricing?.DisplayStatus) : undefined,
    pricingOption: normalizeDisplayStatus(pricing?.DisplayStatus),
    internationalPricingOption: normalizeDisplayStatus(pricing?.InternationalPricingOption, {
      allowed: DISPLAY_STATUS_OPTIONS.INTERNATIONAL.map((opt) => opt.id),
      fallback: DISPLAY_STATUS.DISPLAY_TO_CLIENT,
    }),
    color: pricing?.Color || pricing?.color || pricing?.selectedColor || "",
    size: pricing?.Size || pricing?.size || "",
    isLocked: locked,
    isRetailLocked: retailLocked,
  };
};



const DEFAULT_MARGIN_FALLBACK = 40;
const INTERNATIONAL_PRICE_TYPES = ["Inbound freight", "Brokerage", "Duty"];
const DEFAULT_CURRENCY_SETTINGS = {
  defaultCurrencyCode: "CAD",
  enabledCurrencyCodes: ["CAD"],
  exchangeRates: {},
};

const resolvePriceTypeFromPricing = (p = {}) => {
  if (p?.Type === "BreakDown") return "Breakdown";

  if (p?.SubType === "Run Charge") return "Run Charge";

  // SubType is the authoritative system identity for international rows.
  if (INTERNATIONAL_PRICE_TYPES.includes(p?.SubType)) {
    return p.SubType;
  }

  // Legacy fallback only for older rows where SubType may be missing.
  if (INTERNATIONAL_PRICE_TYPES.includes(p?.CostName)) {
    return p.CostName;
  }

  // Preserve existing behavior:
  // Cost rows with blank/non-run/non-international SubType hydrate as Fixed Charge.
  return "Fixed Charge";
};

const normalizeCurrencySettings = (settings = {}) => {
  const defaultCurrencyCode = String(settings?.defaultCurrencyCode || DEFAULT_CURRENCY_SETTINGS.defaultCurrencyCode).toUpperCase();
  const enabledCurrencyCodes = Array.isArray(settings?.enabledCurrencyCodes)
    ? settings.enabledCurrencyCodes.map((code) => String(code || "").toUpperCase()).filter(Boolean)
    : DEFAULT_CURRENCY_SETTINGS.enabledCurrencyCodes;
  return {
    defaultCurrencyCode,
    enabledCurrencyCodes: Array.from(new Set([defaultCurrencyCode, ...enabledCurrencyCodes])),
    exchangeRates: settings?.exchangeRates || {},
  };
};

const resolvePricingCurrencyCode = (item = {}, currencySettings = DEFAULT_CURRENCY_SETTINGS) => (
  item?.PricingCurrencyCode ||
  currencySettings?.defaultCurrencyCode ||
  item?.CurrencyCode ||
  "CAD"
);

function normalizePromoStoreProductResourceUrl(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizePromoStoreProductResourceName(value) {
  return typeof value === "string" ? value.trim() : "";
}

function isValidPromoStoreProductResourceUrl(value) {
  if (!value) return true;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

const MAX_PROMOSTORE_PRODUCT_RESOURCES = 5;
let promoStoreProductResourceTempIdCounter = 0;

function createPromoStoreProductResource(overrides = {}) {
  promoStoreProductResourceTempIdCounter += 1;
  const tempResourceId =
    overrides.tempResourceId ||
    `promo-store-resource-${promoStoreProductResourceTempIdCounter}`;
  return {
    id: null,
    tempResourceId,
    name: "",
    url: "",
    sortOrder: 0,
    ...overrides,
  };
}

function normalizePromoStoreProductResources(resources = [], fallbackProductResourceUrl = "") {
  const sourceResources = Array.isArray(resources) && resources.length
    ? resources
    : normalizePromoStoreProductResourceUrl(fallbackProductResourceUrl)
      ? [{
          name: "Product Resource",
          url: normalizePromoStoreProductResourceUrl(fallbackProductResourceUrl),
          sortOrder: 0,
        }]
      : [];

  return sourceResources
    .map((resource, index) => {
      const id = Number(resource?.id ?? resource?.PromoStoreItemResourceId ?? resource?.promoStoreItemResourceId);
      const sortOrderRaw = resource?.sortOrder ?? resource?.SortOrder ?? index;
      const tempResourceId = resource?.tempResourceId || resource?.TempResourceId || undefined;
      return createPromoStoreProductResource({
        id: Number.isInteger(id) && id > 0 ? id : null,
        tempResourceId,
        name: normalizePromoStoreProductResourceName(resource?.name ?? resource?.Name),
        url: normalizePromoStoreProductResourceUrl(resource?.url ?? resource?.Url),
        sortOrder: Number.isFinite(Number(sortOrderRaw)) ? Number(sortOrderRaw) : index,
      });
    })
    .filter((resource) => resource.id || resource.tempResourceId || resource.name || resource.url)
    .sort((a, b) => {
      const orderDiff = Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0);
      if (orderDiff !== 0) return orderDiff;
      return String(a.tempResourceId).localeCompare(String(b.tempResourceId));
    })
    .map((resource, index) => ({
      ...resource,
      sortOrder: index,
      tempResourceId: resource.tempResourceId || createPromoStoreProductResource().tempResourceId,
    }));
}

function validatePromoStoreProductResources(resources = []) {
  if ((resources || []).length > MAX_PROMOSTORE_PRODUCT_RESOURCES) {
    return {
      isValid: false,
      errorsByKey: {},
      message: `A maximum of ${MAX_PROMOSTORE_PRODUCT_RESOURCES} product resources is allowed.`,
    };
  }

  const errorsByKey = {};

  (resources || []).forEach((resource) => {
    const key = String(resource?.id || resource?.tempResourceId || "");
    const name = normalizePromoStoreProductResourceName(resource?.name);
    const url = normalizePromoStoreProductResourceUrl(resource?.url);

    if (!name && !url) return;

    const nextError = {};
    if (!name) nextError.name = "Resource Name is required.";
    if (name.length > 100) nextError.name = "Resource Name must be 100 characters or fewer.";
    if (!url) nextError.url = "Resource URL is required.";
    else if (!isValidPromoStoreProductResourceUrl(url)) nextError.url = "Enter a valid http:// or https:// URL.";

    if (Object.keys(nextError).length) errorsByKey[key] = nextError;
  });

  return {
    isValid: Object.keys(errorsByKey).length === 0,
    errorsByKey,
    message: null,
  };
}

function getPersistedPricingId(value) {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
}

const EditProduct = ({
    open,
    fetchOrder,
    initialItem,
    onClose,
    orderItemId,
    order,
    isPromoStore = false,
    controlledMode = false,
    onSaveProduct = null,
    onSaveLifecycleChange = null,
    source = "order-cart",
    promoStoreCategories = [],
    canSeeManualPricingToggle = false,
    supportsPromoStoreArtworkChoices: supportsPromoStoreArtworkChoicesOverride = null,
    cartProducts = [],
    onCopyDecorationToProduct = null,
}) => {
  const inventoryMode = Boolean(isPromoStore);
  const { authState } = useContext(AuthContext);
  const pricingPolicy = useMemo(
    () => resolvePricingPolicy({ isPromoStore: Boolean(isPromoStore) }),
    [isPromoStore]
  );
  // Sales Orders and invoicing carts are still editable working documents.
  // Supplier pack sizes, quantities, pricing, and other details can legitimately
  // change before the final document is committed, so persisted pricing-row
  // locks should not block edits in either workspace.
  // Pricing-row locks are independent of the Sales Order Cart edit-unlock setting.
  // Restore the original lock enforcement and bulk controls for both cart types.
  const respectPricingLocks = true;
  const showNetCostLocks =
    source === "sales-order-cart" || source === "invoicing-cart";
  const isPromoStoreProduct = Boolean(isPromoStore);
  const supportsPromoStoreArtworkChoices =
    typeof supportsPromoStoreArtworkChoicesOverride === "boolean"
      ? supportsPromoStoreArtworkChoicesOverride
      : isPromoStoreProductDecorationEnabled({
          isPromoStoreContext: isPromoStoreProduct,
          templateConfig: order?.TemplateConfig || order?.templateConfig || {},
          store: order,
        });
  const isPromoStoreCart = isPromoStoreProduct && Boolean(controlledMode);
  const [defaultMargin, setDefaultMargin] = useState(DEFAULT_MARGIN_FALLBACK);
  const [currencySettings, setCurrencySettings] = useState(DEFAULT_CURRENCY_SETTINGS);
  const [currencySettingsLoaded, setCurrencySettingsLoaded] = useState(false);
  const debugRender =
    typeof window !== "undefined" &&
    (window.__ELITE_DEBUG_RENDER === true || window.localStorage?.getItem("elite_debug_render") === "1");

  const resolveInstanceUniqueId = () => {
    const raw =
      order?.InstanceUniqueIds ??
      order?.instanceUniqueIds ??
      order?.instanceUniqueId ??
      order?.InstanceUniqueId ??
      null;
    if (!raw) return null;
    if (Array.isArray(raw)) return raw[0] || null;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed[0] || null;
        return parsed || raw;
      } catch {
        return raw;
      }
    }
    return null;
  };

  const toMarginValue = (value) => {
    if (value === "" || value === null || value === undefined) return defaultMargin;
    const n = Number(value);
    return Number.isFinite(n) ? n : defaultMargin;
  };

// Which fields did the user change during this open session?
const touchedRef = useRef({
  decorations:   false,
  pricingRows:   false,
  productResources: false,
  productName:   false,
  description:   false,
  privateNote:   false,
  selectedImage: false, // keep if you let users edit hero image
  imageUrls:     false,
});

// helpers to mark touched + set state
const markTouched = (key) => { touchedRef.current[key] = true; };

const setDecorationsSafe = (updatedRows) => {
  markTouched('decorations');
  setFormData(prev => {
    const prevRows = Array.isArray(prev?.decorations) ? prev.decorations : [];
    const nextRows = typeof updatedRows === 'function' ? updatedRows(prevRows) : updatedRows;
    return {
      ...prev,
      decorations: Array.isArray(nextRows) ? nextRows : prevRows,
    };
  });
};

const setPricingRowsSafe = (updatedOrFn) => {
  markTouched('pricingRows');
  setFormData(prev => {
    const prevRows = Array.isArray(prev.pricingRows) ? prev.pricingRows : [];
    const nextRows = typeof updatedOrFn === 'function' ? updatedOrFn(prevRows) : updatedOrFn;
    const normalized = normalizePricingRowsForEditState(nextRows, prevRows, {
      defaultMargin,
      pricingPolicy,
      respectPricingLocks,
    });

    const obsoleteNormalized = (nextRows || []).map((row, i) => {
      const before = prevRows[i] || {};

      // 🔒 LOCKED → freeze everything except netCost; total = frozen retail × qty
      if (isLockedBool(row?.isLocked)) {
        const frozenQty          = Number(before.quantity ?? row.quantity ?? 0);
        const maybeNewNetCost    = isTransientDecimal(row.netCost)
          ? row.netCost
          : Number((row.netCost ?? before.netCost) ?? 0);
        const frozenRetail       = Number(before.retail ?? row.retail ?? 0);
        const frozenClientPrice  = Number(before.clientPrice ?? row.clientPrice ?? frozenRetail);
        const frozenMargin       = hasConvertedCostBasis(row)
          ? Number(row.margin ?? before.margin ?? defaultMargin)
          : Number(before.margin ?? row.margin ?? defaultMargin);

        const frozenConvertedNetCost = row.convertedNetCost ?? row.ConvertedNetCost ?? before.convertedNetCost ?? before.ConvertedNetCost ?? null;
        const frozenExchangeRate = row.exchangeRate ?? row.ExchangeRate ?? before.exchangeRate ?? before.ExchangeRate ?? null;

        return {
          ...row,
          quantity:    frozenQty,
          netCost:     maybeNewNetCost, // allow transient strings like "."
          convertedNetCost: frozenConvertedNetCost,
          ConvertedNetCost: frozenConvertedNetCost,
          exchangeRate: frozenExchangeRate,
          ExchangeRate: frozenExchangeRate,
          margin:      frozenMargin,
          retail:      frozenRetail,
          clientPrice: frozenClientPrice,
          total:       +((frozenRetail) * frozenQty).toFixed(2),
        };
      }

      // 🔓 UNLOCKED
      const hasTransient =
        isTransientDecimal(row.netCost) ||
        isTransientDecimal(row.margin) ||
        isTransientDecimal(row.retail) ||
        isTransientDecimal(row.clientPrice) ||
        isTransientDecimal(row.total);

      if (hasTransient) return row;

      const coerced = {
        ...row,
        quantity: Number(row.quantity ?? 0),
        netCost:  Number(row.netCost  ?? 0),
        retail:   Number(row.retail   ?? 0),
        margin:   Number(row.margin   ?? defaultMargin),
        convertedNetCost: row.convertedNetCost ?? row.ConvertedNetCost ?? null,
        exchangeRate: row.exchangeRate ?? row.ExchangeRate ?? null,
      };

      return recomputeEditableRow(coerced, defaultMargin, pricingPolicy);


      // otherwise recompute from numeric cost/margin/qty
      return recomputeUnlockedRow({
        ...row,
        quantity: Number(row.quantity ?? 0),
        netCost:  Number(row.netCost  ?? 0),
      });
    });

    void obsoleteNormalized;
    return { ...prev, pricingRows: normalized };
  });
};


const handleFieldChangeSafe = (key, value) => {
  // keys like 'productName', 'description', 'privateNote', 'selectedImage'
  markTouched(key);
  setFormData(prev => ({ ...prev, [key]: value }));
};

const setProductResourcesSafe = (updatedResources) => {
  markTouched("productResources");
  setFormData((prev) => {
    const prevResources = Array.isArray(prev?.productResources) ? prev.productResources : [];
    const nextResources = typeof updatedResources === "function"
      ? updatedResources(prevResources)
      : updatedResources;
    return {
      ...prev,
      productResources: normalizePromoStoreProductResources(nextResources).slice(0, MAX_PROMOSTORE_PRODUCT_RESOURCES),
    };
  });
};

const normalizePromoStoreCategorySelections = (item = {}, availableCategories = []) => {
  const sourceCategories = Array.isArray(item?.categories)
    ? item.categories
    : Array.isArray(item?.Categories)
      ? item.Categories
      : [];
  const categoryIds = Array.isArray(item?.categoryIds)
    ? item.categoryIds
    : Array.isArray(item?.CategoryIds)
      ? item.CategoryIds
      : sourceCategories.map((category) => category?.id);
  const categoriesById = new Map(
    (Array.isArray(availableCategories) ? availableCategories : []).map((category) => [
      String(category?.id),
      category,
    ]),
  );
  const seen = new Set();
  const normalizedCategoryIds = categoryIds
    .map((value) => {
      const parsed = Number(value);
      return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    })
    .filter((value) => {
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
  const normalizedCategories = normalizedCategoryIds
    .map((categoryId) =>
      categoriesById.get(String(categoryId)) ||
      sourceCategories.find((category) => String(category?.id) === String(categoryId)) ||
      null
    )
    .filter(Boolean);

  return {
    categoryIds: normalizedCategoryIds,
    categories: normalizedCategories,
  };
};

// Attribute identity is case-insensitive; dropdown display values preserve user/vendor wording.
// Legacy PRCL is read as Color, while new write paths use COLOR.
const safeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.Values)) return value.Values;
  return [];
};

const getValueName = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  return String(value.Name ?? value.name ?? value.Value ?? value.value ?? value.Label ?? value.label ?? value.id ?? "").trim();
};

const addOption = (map, value) => {
  const name = getValueName(value);
  if (!name) return;
  const key = name.toLowerCase();
  if (!map.has(key)) map.set(key, { id: name, name });
};

const normalizeSkuCode = (code) => {
  const normalized = String(code || "").trim().toUpperCase();
  if (normalized === "PRCL" || normalized === "COLOUR") return "COLOR";
  return normalized;
};

const extractValuesFromAttributeBucket = (attributes, bucketNames = []) => {
  const values = [];
  const attrs = attributes && typeof attributes === "object" ? attributes : {};
  const accepted = new Set(bucketNames.map((bucket) => String(bucket || "").trim().toLowerCase()));
  Object.entries(attrs).forEach(([bucket, bucketValue]) => {
    if (accepted.has(String(bucket || "").trim().toLowerCase())) {
      safeArray(bucketValue).forEach((value) => values.push(value));
    }
  });
  return values;
};

const extractValuesFromSku = (skuRows, acceptedCodes = []) => {
  const accepted = new Set(acceptedCodes.map(normalizeSkuCode));
  return safeArray(skuRows).flatMap((skuRow) =>
    safeArray(skuRow?.Values).filter((value) => accepted.has(normalizeSkuCode(value?.Code))).map(getValueName)
  );
};

const attributeTypeFromVariantAttribute = (attribute = {}) =>
  normalizeSkuCode(attribute.type || attribute.Type || attribute.Code || attribute.code || attribute.name || attribute.Name);

const extractValuesFromVariants = (variants, fieldNames = [], acceptedAttributeCodes = []) => {
  const fields = new Set(fieldNames);
  const accepted = new Set(acceptedAttributeCodes.map(normalizeSkuCode));
  const values = [];
  safeArray(variants).forEach((variant) => {
    fieldNames.forEach((field) => {
      if (variant?.[field] != null) values.push(variant[field]);
    });
    safeArray(variant?.Attributes).forEach((attribute) => {
      if (accepted.has(attributeTypeFromVariantAttribute(attribute))) values.push(attribute.value ?? attribute.Value ?? attribute.Name ?? attribute.name);
    });
    Object.entries(variant || {}).forEach(([key, value]) => {
      if (fields.has(key)) return;
      if (accepted.has(normalizeSkuCode(key))) values.push(value);
    });
  });
  return values;
};

const collectProductOptionCandidates = (initialItem, extraProductData = null) => ([
  initialItem?.asiLiveRawData,
  initialItem?.product,
  initialItem?.Product,
  initialItem?.customProduct,
  initialItem?.CustomProduct,
  initialItem?.rawProduct,
  initialItem?.RawProduct,
  initialItem?.ProductData,
  initialItem?.productData,
  initialItem?.ProductDetails,
  initialItem?.productDetails,
  extraProductData,
  extraProductData?.asiLiveRawData,
  extraProductData?.product,
  extraProductData?.Product,
  extraProductData?.customProduct,
  extraProductData?.CustomProduct,
  extraProductData?.rawProduct,
  extraProductData?.RawProduct,
  extraProductData?.ProductData,
  extraProductData?.productData,
  extraProductData?.ProductDetails,
  extraProductData?.productDetails,
  initialItem,
]).filter(Boolean);

const extractSkuOptionsFromProducts = (products = [], pricingRows = []) => {
  const skuMap = new Map();
  const addSku = (value) => addOption(skuMap, value);
  products.forEach((product) => {
    addSku(product?.Number);
    addSku(product?.SKU);
    addSku(product?.sku);
    safeArray(product?.Sku).forEach((row) => {
      addSku(row?.SKU);
      addSku(row?.Number);
      addSku(row?.sku);
    });
    safeArray(product?.SKU).forEach((row) => {
      addSku(row?.SKU);
      addSku(row?.Number);
      addSku(row?.sku);
    });
    safeArray(product?.Variants).forEach((variant) => {
      addSku(variant?.SKU);
      addSku(variant?.Number);
      addSku(variant?.sku);
    });
    safeArray(product?.Numbers).forEach(addSku);
    safeArray(product?.Attributes?.Sizes?.Values).forEach((value) => addSku(value?.ProductNumber));
  });
  safeArray(pricingRows).forEach((row) => addSku(row?.SKU || row?.sku || row?.Number || row?.number));
  return Array.from(skuMap.values());
};

const extractProductOptionsForEditItem = (initialItem, pricingRows = [], extraProductData = null) => {
  const products = collectProductOptionCandidates(initialItem, extraProductData);
  const colorMap = new Map();
  const sizeMap = new Map();

  products.forEach((product) => {
    extractValuesFromAttributeBucket(product?.Attributes, ["Colors", "Color", "Colours", "Colour"]).forEach((value) => addOption(colorMap, value));
    extractValuesFromAttributeBucket(product?.Attributes, ["Sizes", "Size"]).forEach((value) => addOption(sizeMap, value));
    safeArray(product?.Colors || product?.colors).forEach((value) => addOption(colorMap, value));
    safeArray(product?.Sizes || product?.sizes).forEach((value) => addOption(sizeMap, value));
    extractValuesFromSku(product?.Sku || product?.SKU, ["COLOR", "PRCL"]).forEach((value) => addOption(colorMap, value));
    extractValuesFromSku(product?.Sku || product?.SKU, ["SIZE"]).forEach((value) => addOption(sizeMap, value));
    extractValuesFromVariants(product?.Variants, ["Color", "Colour", "color", "colour"], ["COLOR", "COLOUR", "PRCL"]).forEach((value) => addOption(colorMap, value));
    extractValuesFromVariants(product?.Variants, ["Size", "size"], ["SIZE"]).forEach((value) => addOption(sizeMap, value));
  });

  safeArray(pricingRows).forEach((row) => {
    addOption(colorMap, row?.Color || row?.color || row?.selectedColor);
    addOption(sizeMap, row?.Size || row?.size);
  });

  return {
    colorOptions: Array.from(colorMap.values()),
    sizeOptions: Array.from(sizeMap.values()),
    skuOptions: extractSkuOptionsFromProducts(products, pricingRows),
  };
};

const extractVariationOptionsForEditItem = (initialItem, extraProductData = null) => {
  const products = collectProductOptionCandidates(initialItem, extraProductData);
  const variationMap = new Map();

  const getStructuredFieldValue = (source, fieldNames = [], acceptedCodes = []) => {
    if (!source || typeof source !== "object") return "";

    for (const fieldName of fieldNames) {
      const value = getValueName(source?.[fieldName]);
      if (value) return value;
    }

    const accepted = new Set(acceptedCodes.map(normalizeSkuCode));

    for (const attribute of safeArray(source?.Attributes)) {
      if (accepted.has(attributeTypeFromVariantAttribute(attribute))) {
        const value = getValueName(attribute?.value ?? attribute?.Value ?? attribute?.Name ?? attribute?.name);
        if (value) return value;
      }
    }

    for (const value of safeArray(source?.Values)) {
      if (accepted.has(normalizeSkuCode(value?.Code))) {
        const resolved = getValueName(value);
        if (resolved) return resolved;
      }
    }

    for (const [key, rawValue] of Object.entries(source)) {
      if (accepted.has(normalizeSkuCode(key))) {
        const value = getValueName(rawValue);
        if (value) return value;
      }
    }

    return "";
  };

  const addVariation = ({ color = "", size = "", sku = "" } = {}) => {
    const normalizedColor = String(color || "").trim();
    const normalizedSize = String(size || "").trim();

    if (!normalizedColor || !normalizedSize) return;

    const key = `${normalizedColor.toLowerCase()}::${normalizedSize.toLowerCase()}`;
    const existing = variationMap.get(key);

    if (!existing) {
      variationMap.set(key, {
        color: normalizedColor,
        size: normalizedSize,
        sku: String(sku || "").trim(),
      });
      return;
    }

    if (!existing.sku && sku) {
      variationMap.set(key, {
        ...existing,
        sku: String(sku).trim(),
      });
    }
  };

  products.forEach((product) => {
    safeArray(product?.Variants).forEach((variant) => {
      addVariation({
        color: getStructuredFieldValue(
          variant,
          ["Color", "Colour", "color", "colour"],
          ["COLOR", "COLOUR", "PRCL"]
        ),
        size: getStructuredFieldValue(
          variant,
          ["Size", "size"],
          ["SIZE"]
        ),
        sku: variant?.SKU || variant?.sku || variant?.Number || variant?.number || "",
      });
    });

    [...safeArray(product?.Sku), ...safeArray(product?.SKU)].forEach((skuRow) => {
      addVariation({
        color: getStructuredFieldValue(
          skuRow,
          ["Color", "Colour", "color", "colour"],
          ["COLOR", "COLOUR", "PRCL"]
        ),
        size: getStructuredFieldValue(
          skuRow,
          ["Size", "size"],
          ["SIZE"]
        ),
        sku: skuRow?.SKU || skuRow?.sku || skuRow?.Number || skuRow?.number || "",
      });
    });
  });

  return Array.from(variationMap.values());
};


// optional: reset the touched map whenever dialog opens fresh
useEffect(() => {
  if (open) {
    touchedRef.current = {
      decorations: false, pricingRows: false, productResources: false,
      productName: false, description: false, privateNote: false, selectedImage: false,
    };
  }
}, [open]);

// ===== DEBUG HELPERS (safe to leave in dev) =====
const __DBG = () =>
  typeof window !== "undefined" && (window.__DBG_DECOS ?? true); // flip with window.__DBG_DECOS=false

const hasAnyArtwork = (d) =>
  !!(
    d?.artwork?.selectedImage ||
    d?.ArtWork?.Image ||
    (Array.isArray(d?.rows) &&
      d.rows.some(r => r?.artwork?.selectedImage || r?.ArtWork?.Image))
  );

  const toNum = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const resolveInitialSelectedTax = (item, promoStoreProduct = false) => {
    if (!promoStoreProduct) {
      return (item?.taxDetails?.taxId ?? "").toString();
    }

    return (
      item?.taxDetails?.taxId ??
      item?.taxId ??
      item?.Tax ??
      item?.selectedTax ??
      item?.taxIds?.[0] ??
      ""
    ).toString();
  };

  const resolveInitialSelectedTaxRate = (
    item,
    promoStoreProduct = false,
    availableTaxOptions = []
  ) => {
    if (!promoStoreProduct) {
      return toNum(item?.taxDetails?.value ?? item?.taxRate?.value);
    }

    const selectedTax = resolveInitialSelectedTax(item, promoStoreProduct);
    const option = findTaxOptionById(availableTaxOptions, selectedTax);
    const optionRate = normalizeRateDecimal(
      option?.value ?? option?.rate ?? option?.Rate
    );

    return toNum(
      optionRate ||
        (
          item?.taxDetails?.value ??
          item?.taxDetails?.rate ??
          item?.taxRate?.value ??
          item?.selectedTaxRate
        )
    );
  };

const briefDecoration = (d) => ({
  id: d?.id || "",
  loc: d?.Location || "",
  locArt: d?.artwork?.selectedImage || d?.ArtWork?.Image || null,
  rowArtCount: Array.isArray(d?.rows)
    ? d.rows.filter(r => r?.artwork?.selectedImage || r?.ArtWork?.Image).length
    : 0,
  source: d?.DecoratorDetails?.source || "",
});

const logDecos = (label, arr, extra = {}) => {
  if (!__DBG()) return;
  const a = Array.isArray(arr) ? arr : [];
  console.groupCollapsed(`🎯 ${label} [${a.length}]`, extra);
  console.log("any artwork?:", a.some(hasAnyArtwork));
  a.slice(0, 5).forEach((d, i) => console.log(`#${i}`, briefDecoration(d)));
  console.groupEnd();
};

const deriveDecorationLocations = (decorations = []) => {
  const rows = Array.isArray(decorations) ? decorations : [];
  const out = [];
  const seen = new Set();

  rows.forEach((d) => {
    const name = String(d?.Location || "").trim();
    if (!name) return;

    const key = name.toLowerCase();
    if (seen.has(key)) return;

    const imageUrl = d?.artwork?.selectedImage || d?.image || "";
    out.push({ name, imageUrl });
    seen.add(key);
  });

  return out;
};


    //console.log("🚀 EditProduct initialItem:", initialItem);
    // UI-only helpers for showing ID + label
    const getIdLabelAndValue = (source, rawId) => {
       const normalizedSource = String(source || "").trim().toLowerCase();
       if (normalizedSource === "custom") {
         return { label: "Custom ID", value: `${CUSTOM_PREFIX}${rawId}` };
       }
       if (normalizedSource === "promoworld") {
         return { label: "PromoWorld ID", value: rawId || "" };
       }
       return { label: "ASI ID", value: rawId || "" };
    };

    const getPersistedDecorationId = (value) => {
      const numeric = Number(value);
      return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
    };

    const [formData, setFormData] = useState({
        // ─────────────── Images ───────────────
        // single “hero” image
        selectedImage: "",
        // all images for the FilesPopup carousel
        imageUrls:     [],

        // ────────────── Basic Info ──────────────
        productName:  "",
        supplier:     "",
        supplierId:   null,
        asiId:        "",
        CurrencyCode: "",
        PricingCurrencyCode: "",
        pricingGridMode: false,
        storefrontPricingRules: normalizeStorefrontPricingRules({}),

        // ─────────── Descriptions ───────────
        description:  "",
        privateNote:  "",
        categoryIds: [],
        categories: [],

        // ───────────── Pricing ─────────────
        // your main pricing tiers
        pricingRows: [
          {
            type:                   "",
            priceType:              "Breakdown",
            chargeName:             "",
            asiId:                  "",
            sku: "",
            quantity:               1,
            inventoryQuantity:      null,
            netCost:                0,
            margin:                 DEFAULT_MARGIN_FALLBACK,
            retail:                 0,
            clientPrice:            0,
            total:                  0,
            inventoryTracking:      inventoryMode ? INVENTORY_TRACKING.track : undefined,
            pricingOption:          "Display to Client",
            internationalPricingOption: "Display to Client",
            color:                  "",
            size:                   ""
          }
        ],

        // ─────────── Decorations ────────────
        // array of { id, Location, DecoratorDetails, rows:[…], ArtWork:{…} }
        decorations:   [],
        // (if you ever edit location separately)
        selectedLocation: "",

        // ─────────── PromoStore Decoration Add-on ────────────
        productionDecorationLocations: [],
        productionDecorationDeriveFromUserAddresses: false,

        // ─────────── Artwork ────────────
        // if you need a top‐level blob
        artworks:      [],

        // ─────────── Tax ────────────
        selectedTax:  null,

        // ─────────── Links ────────────
        // “view on supplier’s site”
        productUrl:   "",
        // “view on Elite Core”
        coreUrl:      ""
      });

    const [loading, setLoading] = useState(false)
    const [itemloading, setItemLoading] = useState(false)
    const [clientFiles, setClientFiles] = useState([])
    const [sizeOptions, setSizeOptions] = useState([]);
    const [colorOptions, setColorOptions] = useState([]);
    const [variationOptions, setVariationOptions] = useState([]);
    const [orderItem, setOrderItem] = useState(null)
    const [taxOptions, setTaxOptions] = useState([]);
    const [asiLiveRawData, setAsiLiveRawData] = useState(null);
    const [decorationString, setDecorationString] = useState(null);
    const [skuOptions, setSkuOptions] = useState([]);

    // background-mapped decorations
    const [bgDecorations, setBgDecorations] = useState(null);     // null until ready
    const [bgDecosLoading, setBgDecosLoading] = useState(false);
    const [bgDecosError, setBgDecosError] = useState(null);

    const [artworkPending, setArtworkPending] = useState(0);
    const artworkLocks = useMemo(() => ({
      lock: () => setArtworkPending(c => c + 1),
      unlock: () => setArtworkPending(c => Math.max(0, c - 1)),
      reset: () => setArtworkPending(0)
    }), []);
    const isArtworkLocked = artworkPending > 0;

  useEffect(() => {
    if (!debugRender) return;
    const componentPresence = {
      Dialog,
      DialogTitle,
      DialogContent,
      DialogActions,
      Button,
      Box,
      ItemDetails,
      PricingBreakDown,
      Decoration,
      PricingSummary,
      LoadingComponent,
    };
    const undefinedComponents = Object.entries(componentPresence)
      .filter(([, value]) => typeof value === "undefined")
      .map(([name]) => name);

    console.log("[EditProduct] render checkpoint", {
      open,
      orderItemId,
      loading,
      itemloading,
      isArtworkLocked,
      pricingRows: formData?.pricingRows?.length || 0,
      decorations: formData?.decorations?.length || 0,
      undefinedComponents,
    });
  }, [debugRender, open, orderItemId, loading, itemloading, isArtworkLocked, formData?.pricingRows?.length, formData?.decorations?.length]);

    // Background map for OTHER products' decorations (never overwrites formData.decorations)
    const [decorationEntirety, setDecorationEntirety] = useState({
       byItemId: {},   // { [itemId]: { status: 'idle'|'loading'|'ready'|'error', raw: any, mapped: any, error?: any } }
       bootstrapped: false
    });

     const mapDecorationsSafe = (raw, defaultLocation, sourceOptions) => {
           try {
             const arr = Array.isArray(raw) ? raw : (typeof raw === "string" ? JSON.parse(raw) : []);
             return mapDecorationsFromApi(arr, defaultLocation, sourceOptions) || [];
           } catch (e) {
             return [];
           }
      };

    const imprintMethods = useMemo(
      () => asiLiveRawData?.Imprinting?.Methods?.Values || [],
      [asiLiveRawData]
    );
    const uniqueImprintMethods = imprintMethods.filter((method, index, allMethods) =>
      allMethods.findIndex((candidate) => candidate.Code === method.Code) === index
    );

    const liveSourceOptions = buildSupplierDecorationSourceOptions(asiLiveRawData);
    const sourceOptions = buildSavedDecorationSourceOptions(
      formData?.decorations || initialItem?.decorations || initialItem?.Decorations || [],
      liveSourceOptions
    );

    // single source of truth for your dropdown
    const [decorationSource, setDecorationSource] = useState(null);

    // clear selection whenever the popup opens
    useEffect(() => {
    if (open) setDecorationSource(null);
    }, [open]);

    // seed $UNI (or fallback) once per open+data load
    useEffect(() => {
        // 1️⃣ only run when the dialog is open
        if (!open) return;

        // 2️⃣ wait for your ASI fetch to complete
        if (asiLiveRawData === null) return;

        // 3️⃣ don’t reseed if the user has already chosen something
        if (decorationSource) return;

        // 4️⃣ grab the array of methods (might be empty)
        const imprintMethods = asiLiveRawData.Imprinting?.Methods?.Values || [];

        const availableMethods = imprintMethods.filter(m =>
          String(m?.Code || '').trim().toUpperCase() !== '$UNI' &&
          !/^unimprinted$/i.test(String(m?.Name || '').trim())
        );
        if (availableMethods.length > 0) {
          const method = availableMethods[0];

          setDecorationSource({
            type:       'supplier',
            methodCode: method.Code,
            value:      `supplier|${method.Code}`
          });
        } else {
          // no suppliers at all → fallback to in-house
          setDecorationSource({ type: 'elite', value: 'elite' });
        }
      }, [open, asiLiveRawData, decorationSource]);



    const fetchTermsAndTaxes = async () => {
        try {
            const taxesData = await getTaxes();
            setTaxOptions(taxesData?.taxes || []);
        } catch (error) {
        }
    };
    /*const mapDecorations = (decorations) =>
    (decorations || []).map((decoration) => ({
      id: decoration.id,
      Location: decoration.Location || "",
      DecoratorDetails: {
        ...(decoration.DecoratorDetails || {}),
        source:     decoration.DecoratorDetails?.source     || "",
        methodName: decoration.DecoratorDetails?.methodName || ""
      },
      rows: (decoration.Charge || []).map((charge) => ({
        chargeName:    charge.Name           || "",
        quantity:      charge.Quantity       || 0,
        netCost:       charge.NetCost        || 0,
        margin:        charge.Margin         || 0,
        retail:        charge.Retail         || 0,
        clientPrice:   charge.ClientPrice    || 0,
        total:         charge.Total          || 0,
        type:          charge.Type           || "Fixed Charge",
        pricingOption: charge.DisplayStatus  || "Display to Client",
        supplierId:    charge.supplierId     || null,
        addressId:     charge.addressId      || null,
      })),
      ArtWork: {
        ImprintType:   decoration.artwork?.imprintType    || "",
        CustomerFileId:decoration.artwork?.customerFileId || null,
        Charge:        decoration.artwork?.charge         || 0,
        ProofRequired: decoration.artwork?.proofRequired  || "",
        LogoName:      decoration.artwork?.logoName       || "",
        LogoSize:      decoration.artwork?.logoSize       || "",
        LogoColor:     decoration.artwork?.logoColor      || "",
        RepeatLogo:    decoration.artwork?.repeatLogo     || "",
        SupplierNotes: decoration.artwork?.supplierNotes  || "",
        Image:         decoration.artwork?.selectedImage  || null
      }
    }));*/


  // ─────────────────────────────────────────────────────────────────────────────
  // then, replace your existing useEffect(...) with this one
  // ─────────────────────────────────────────────────────────────────────────────
  const idDep = `${formData.productSource || 'asi'}:${formData.rawProductId || ''}`;
  useEffect(() => {
    if (!open || !initialItem) return;
    //if (formData.asiId === initialItem.ProductId) return;
    // # POTENTIAL BUG FIXED ^ by removing this line
    // If add/edit products doesn't work well, uncomment above live, prior decorations data wasn't pulling through
    // on second load - DR

    setItemLoading(true);

    // Stash the rich supplier/catalogue payload used by pricing/options/decoration UI.
    // PromoWorld carries the same kind of supplier metadata without being an ASI product.
    const initialSource = String(initialItem?.Source || initialItem?.source || initialItem?.LookupType || initialItem?.lookupType || "").toLowerCase();
    const initialRichProductData =
      initialItem.asiLiveRawData ||
      initialItem.ProductData ||
      initialItem.productData ||
      (initialSource === "promoworld" ? initialItem : {});
    setDecorationString(initialItem.decorations || []);
    setAsiLiveRawData(initialRichProductData || {});

    try {
         const { colorOptions: co, sizeOptions: so, skuOptions: skuOpts } = extractProductOptionsForEditItem(initialItem, initialItem.pricings || []);
         const variationOpts = extractVariationOptionsForEditItem(initialItem);
         console.groupCollapsed("🎨 Product options for Edit Item (initialItem)");
         console.log("sizes", so?.slice(0,5));
         console.log("colors", co?.slice(0,5));
         console.log("skus", skuOpts?.slice(0,5));
         console.log("variations", variationOpts?.slice(0,5));
         console.groupEnd();
         setColorOptions(co);
         setSizeOptions(so);
         setSkuOptions(skuOpts);
         setVariationOptions(variationOpts);
       } catch (e) {
         console.warn("No product options found for size/color", e);
         setVariationOptions([]);
    }

    setOrderItem(initialItem);
    const parsed = parseOrderProductId(initialItem.ProductId || "");
    const source = initialItem.Source ?? parsed.source;
    const rawId  = initialItem.RawProductId ?? parsed.id;   const defaultLocation = initialItem.Imprinting?.Locations?.Values?.[0]?.Name || "";
    const initialDecorations = initialItem.decorations || initialItem.Decorations || initialItem.Decoration || [];
    const initialArtworks = initialItem.artworks || initialItem.Artworks || initialItem.ArtWorks || [];
    const decorationsWithArtwork = attachArtworksToDecorations(initialDecorations, initialArtworks);
    const hydrationSourceOptions = buildSavedDecorationSourceOptions(
      decorationsWithArtwork,
      buildSupplierDecorationSourceOptions(initialRichProductData)
    );
    const decoFromApi = mapDecorationsFromApi(
        decorationsWithArtwork,
        defaultLocation,
        hydrationSourceOptions
    );

    const promoWorldVariantPrices =
      initialSource === "promoworld" && Array.isArray(initialRichProductData?.Variants)
        ? (initialRichProductData.Variants[0]?.Prices || [])
        : [];
    const initialPricingSource =
      Array.isArray(initialItem.pricings) && initialItem.pricings.length
        ? initialItem.pricings
        : promoWorldVariantPrices.map((priceBreak, index) => {
            const quantity = Number(priceBreak?.Quantity?.From ?? 1) || 1;
            const netCost = Number(priceBreak?.Cost ?? 0) || 0;
            const explicitRetail = Number(priceBreak?.Price);
            const margin = defaultMargin;
            const retail = Number.isFinite(explicitRetail) && explicitRetail > 0
              ? explicitRetail
              : (margin < 100 ? +(netCost / (1 - margin / 100)).toFixed(2) : 0);
            const variant = initialRichProductData?.Variants?.[0] || {};
            return {
              Type: "BreakDown",
              CostName: priceBreak?.CostName || `${quantity}+`,
              ProductId: initialItem.ProductId,
              SKU: variant?.SKU || initialItem?.SKU || "",
              Quantity: quantity,
              NetCost: netCost,
              Margin: margin,
              Retail: retail,
              ClientPrice: retail,
              Total: +(retail * quantity).toFixed(2),
              CurrencyCode: priceBreak?.CurrencyCode || variant?.CurrencyCode || initialItem?.CurrencyCode || "CAD",
              DisplayStatus: "Display to Client",
              Color: variant?.Color || initialItem?.selectedColor || "",
              Size: variant?.Size || initialItem?.Size || "",
              SortOrder: index,
            };
          });

    const initialSelectedTax = resolveInitialSelectedTax(initialItem, isPromoStoreProduct);
    const promoStoreTaxState = isPromoStoreProduct
      ? buildPromoStoreTaxState(
          {
            ...initialItem,
            selectedTax: initialSelectedTax,
            selectedTaxRate: resolveInitialSelectedTaxRate(
              initialItem,
              isPromoStoreProduct,
              taxOptions
            ),
          },
          taxOptions
        )
      : null;

    // BEFORE setFormData({ ... })
    logDecos("INITIAL raw decorations (initialItem.decorations)", initialItem.decorations || []);
    logDecos("INITIAL mapped → formData.decorations", decoFromApi, {
    defaultLocation,
    sourceOptionsCount: hydrationSourceOptions.length
    });

    const normalizedPromoStoreImages = isPromoStoreProduct
      ? normalizePromoStoreImageSelection({
          selectedImage: initialItem.ImageUrl,
          imageUrls: initialItem.ImageUrls,
        })
      : {
          selectedImage: initialItem.ImageUrl || "",
          imageUrls: Array.isArray(initialItem.ImageUrls) ? initialItem.ImageUrls : [],
        };

    // build a complete, correct formData in one go:
    setFormData({
        // ─────────────── Images ───────────────
        selectedImage: normalizedPromoStoreImages.selectedImage,
        imageUrls:     normalizedPromoStoreImages.imageUrls,

        productIdKey: initialItem.ProductId, // "CUSTOM-5919231" or "1813181"
        rawProductId: rawId,                 // "5919231" or "1813181"
        SKU:          initialItem.SKU || "",
        productSource: source,
        CurrencyCode: initialItem.CurrencyCode || "",
        PricingCurrencyCode: initialItem.PricingCurrencyCode || "",
        pricingGridMode: Boolean(
          initialItem?.pricingGridMode ??
          initialItem?.PricingGridMode ??
          initialItem?.pricingViewConfig?.gridMode ??
          initialItem?.PricingViewConfig?.gridMode ??
          false
        ),
        storefrontPricingRules: normalizeStorefrontPricingRules(
          initialItem?.StorefrontPricingRules ??
          initialItem?.storefrontPricingRules ??
          {}
        ),

        // ────────────── Basic Info ──────────────
        productName:   initialItem.Name           || "",
        supplier:
          initialItem.SupplierName ||
          initialItem.supplierName ||
          initialItem.Supplier?.Name ||
          initialItem.supplier?.name ||
          "",
        supplierId:
          initialItem.SupplierId ??
          initialItem.supplierId ??
          initialItem.Supplier?.Id ??
          initialItem.supplier?.id ??
          null,
        asiId:         rawId      || "",

        // ─────────── Descriptions ───────────
        description:   initialItem.Description    || "",
        privateNote:   initialItem.ShortDescription || "",
        productResources: normalizePromoStoreProductResources(
          initialItem?.resources ?? initialItem?.Resources,
          initialItem?.ProductResourceUrl ??
            initialItem?.productResourceUrl ??
            initialItem?.SizeChartUrl ??
            initialItem?.sizeChartUrl ??
            ""
        ),
        ...normalizePromoStoreCategorySelections(initialItem, promoStoreCategories),

        // ───────────── Pricing ─────────────
legacyPricingRows: initialPricingSource.map(p => {
  const locked    = Boolean(p.IsLocked);
  const hasRetail = p?.Retail !== undefined && p?.Retail !== null && p?.Retail !== "";

  const resolvedMargin = toMarginValue(p.Margin);
  const expectedRetail =
    resolvedMargin < 100
      ? +((Number(p.NetCost || 0) / (1 - (resolvedMargin / 100))).toFixed(2))
      : 0;

  // ✅ align with cart: trust Retail if locked OR Retail is explicitly present
  const retail = (locked || hasRetail)
    ? Number(p.Retail || 0)
    : expectedRetail;

  return {
    id:                        getPersistedPricingId(p.id),

    type:                      p.Type || "",
    priceType:                 resolvePriceTypeFromPricing(p),
    chargeName:                p.CostName || "",
    asiId:                     p.ProductId || initialItem.ProductId,
    sku:                       p.SKU || p.sku || "",
    quantity:                  p.Quantity || 0,
    inventoryQuantity:
      p.InventoryQuantity === null || p.InventoryQuantity === undefined
        ? null
        : Number(p.InventoryQuantity),
    netCost:                   p.NetCost || 0,
    convertedNetCost:          p.ConvertedNetCost ?? null,
    exchangeRate:              p.ExchangeRate ?? null,
    margin:                    resolvedMargin,
    retail,                                  // ← now matches cart
    clientPrice:               p.ClientPrice || retail,
    total:                     p.Total || +(retail * (p.Quantity || 0)).toFixed(2),
    inventoryTracking:         inventoryMode ? normalizeInventoryTracking(p.DisplayStatus) : undefined,
    pricingOption:             normalizeDisplayStatus(p.DisplayStatus),
    internationalPricingOption: normalizeDisplayStatus(p.InternationalPricingOption, {
      allowed: DISPLAY_STATUS_OPTIONS.INTERNATIONAL.map((opt) => opt.id),
      fallback: DISPLAY_STATUS.DISPLAY_TO_CLIENT,
    }),
    color:                     p.Color || p.color || p.selectedColor || "",
    size:                      p.Size || p.size || "",
    isLocked:                  locked,
    isRetailLocked:            isLockedBool(p?.IsRetailLocked ?? p?.isRetailLocked),
  };
}),





        // ─────────── Decorations ────────────
        pricingRows: sortPricingRowsForHydration(
          initialPricingSource.map((p) =>
            buildEditablePricingRow(p, {
              inventoryMode,
              defaultMargin,
              pricingPolicy,
              asiProductId: initialItem.ProductId,
            })
          )
        ),
        decorations: decoFromApi,

        // ─────────── PromoStore Decoration Add-on ────────────
        productionDecorationLocations:
          initialItem.productionDecorationLocations ||
          initialItem.DecorationLocations ||
          [],
        productionDecorationDeriveFromUserAddresses:
          Boolean(
            initialItem.productionDecorationDeriveFromUserAddresses ??
              initialItem.DecorationDeriveFromUserAddresses
          ),

        // ─────────── Artwork ────────────
        artworks:    initialItem.artworks        || [],

        // ─────────── Tax ────────────
        selectedTax: promoStoreTaxState?.selectedTax ?? initialSelectedTax,
        selectedTaxRate:
          promoStoreTaxState?.selectedTaxRate ??
          resolveInitialSelectedTaxRate(initialItem, isPromoStoreProduct, taxOptions),
        ...(isPromoStoreProduct
          ? {
              taxId:
                promoStoreTaxState?.taxId ??
                (Number(initialSelectedTax) || null),
              taxIds:
                promoStoreTaxState?.taxIds ??
                (initialSelectedTax ? [Number(initialSelectedTax)] : []),
            }
          : {}),
        // ─────────── Links / Templates ────────────
        artworkTemplates: initialItem.ArtworkTemplates || [],
        templates:        initialItem.Templates        || []
      });


    setItemLoading(false);
  }, [open, initialItem, idDep, isPromoStoreProduct, taxOptions]);

  useEffect(() => {
    if (!open || !isPromoStoreProduct || !taxOptions.length) return;

    setFormData((prev) => {
      const nextTaxState = buildPromoStoreTaxState(prev, taxOptions);
      const changed =
        nextTaxState.selectedTax !== (prev?.selectedTax ?? "") ||
        nextTaxState.selectedTaxRate !== (prev?.selectedTaxRate ?? null) ||
        nextTaxState.taxId !== (prev?.taxId ?? null) ||
        JSON.stringify(nextTaxState.taxIds) !== JSON.stringify(prev?.taxIds || []);

      if (!changed) return prev;
      return { ...prev, ...nextTaxState };
    });
  }, [open, isPromoStoreProduct, taxOptions]);

    const fetchClientFiles = async (customerId) => {
        try {
            const customerFilesReponse = await getCustomerFiles(customerId)
            setClientFiles(customerFilesReponse?.data?.files)
        } catch (error) {

        }
    }
    const fetchOrderItemDetails = async (orderItemId) => {
        try {
            setItemLoading(true)
            let response = await getOrderItem(orderItemId)
            // If ASI / Revise when adding custom products
            let pureAsiProductData = response?.data?.orderItem?.asiLiveRawData
            logDecos("FETCHED raw decorations (getOrderItem)", response?.decorations || []);

            response = response?.data?.orderItem
            const defaultLocation = pureAsiProductData?.Imprinting?.Locations?.Values?.[0]?.Name || ''


            const responseDecorations = response?.decorations || response?.Decorations || response?.Decoration || [];
            const responseArtworks = response?.artworks || response?.Artworks || response?.ArtWorks || [];
            const hydrationSourceOptions = buildSupplierDecorationSourceOptions(pureAsiProductData);
            const decorationsMapped = mapDecorationsFromApi(
              attachArtworksToDecorations(responseDecorations, responseArtworks),
              defaultLocation,
              hydrationSourceOptions
            );
            setAsiLiveRawData(pureAsiProductData)
            const transformedPricingRows = (response?.pricings || []).map((p) => {
              const locked    = Boolean(p?.IsLocked);
              const hasRetail = p?.Retail !== undefined && p?.Retail !== null && p?.Retail !== "";

              const resolvedMargin = toMarginValue(p?.Margin);
              const expectedRetail =
                resolvedMargin < 100
                  ? +((Number(p?.NetCost || 0) / (1 - (resolvedMargin / 100))).toFixed(2))
                  : 0;

              const retail = (locked || hasRetail)
                ? Number(p?.Retail || 0)
                : expectedRetail;

              return {
                id: p?.id,
                type: p?.Type || "",
                color: p?.Color || p?.color || p?.selectedColor || "",
                size: p?.Size || p?.size || "",
                sku: p?.SKU || p?.sku || "",
                priceType: resolvePriceTypeFromPricing(p),
                chargeName: p?.CostName || "",
                asiId: response?.ProductId || "",
                quantity: p?.Quantity || 1,
                netCost: p?.NetCost || 0,
                convertedNetCost: p?.ConvertedNetCost ?? null,
                exchangeRate: p?.ExchangeRate ?? null,
                margin: resolvedMargin,
                retail,                                 // ← now matches cart
                clientPrice: retail,
                total: +(retail * (p?.Quantity || 1)).toFixed(2),
                inventoryTracking: inventoryMode ? normalizeInventoryTracking(p?.DisplayStatus) : undefined,
                pricingOption: normalizeDisplayStatus(p?.DisplayStatus),
                internationalPricingOption: normalizeDisplayStatus(
                  p?.InternationalPricingOption,
                  {
                    allowed: DISPLAY_STATUS_OPTIONS.INTERNATIONAL.map((opt) => opt.id),
                    fallback: DISPLAY_STATUS.DISPLAY_TO_CLIENT,
                  }
                ),
                addressId: p?.addressId || null,
                supplierId: p?.supplierId || null,
                isLocked: locked,
                isRetailLocked: isLockedBool(p?.IsRetailLocked ?? p?.isRetailLocked),
              };
            });
            const normalizedTransformedPricingRows = (response?.pricings || []).map((p) => ({
              ...buildEditablePricingRow(p, {
                inventoryMode,
                defaultMargin,
                pricingPolicy,
                asiProductId: response?.ProductId || "",
              }),
              addressId: p?.addressId || null,
              supplierId: p?.supplierId || null,
            }));




            const parsed = parseOrderProductId(response?.ProductId || "");
            const source = response?.Source ?? parsed.source;
            const rawId  = response?.RawProductId ?? parsed.id;
            const normalizedPromoStoreImages = isPromoStoreProduct
              ? normalizePromoStoreImageSelection({
                  selectedImage: response?.ImageUrl,
                  imageUrls: response?.ImageUrls,
                })
              : {
                  selectedImage: response?.ImageUrl || "",
                  imageUrls: Array.isArray(response?.ImageUrls) ? response.ImageUrls : [],
                };

            const normIdName = (arr) =>
            (arr || []).map(v => {
              if (v && typeof v === "object" && "id" in v && "name" in v) return v;
              const name = typeof v === "string" ? v : (v?.Name ?? v?.name ?? "");
              return { id: name, name };
            });


            setFormData(prev => {
              const keepDecorations = touchedRef.current.decorations;


                const keepPricing     = touchedRef.current.pricingRows
                  || (Array.isArray(prev.pricingRows) && prev.pricingRows.length > 0);

                const keepName        = touchedRef.current.productName;
                const keepDesc        = touchedRef.current.description;
                const keepPrivate     = touchedRef.current.privateNote;
                const keepProductResources = touchedRef.current.productResources;
                const keepHeroImage   = touchedRef.current.selectedImage;
                const keepImageGallery = touchedRef.current.imageUrls;

              return {
                ...prev,
                selectedImage: keepHeroImage ? prev.selectedImage : normalizedPromoStoreImages.selectedImage,
                imageUrls: keepImageGallery ? prev.imageUrls : normalizedPromoStoreImages.imageUrls,
                productName:   keepName      ? prev.productName   : (response?.Name || ""),
                description:   keepDesc      ? prev.description   : (response?.Description || ""),
                privateNote:   keepPrivate   ? prev.privateNote   : (response?.ShortDescription || ""),
                productResources: keepProductResources
                  ? prev.productResources
                  : normalizePromoStoreProductResources(
                    response?.resources ?? response?.Resources,
                    response?.ProductResourceUrl ??
                      response?.productResourceUrl ??
                      response?.SizeChartUrl ??
                      response?.sizeChartUrl ??
                      ""
                  ),





                supplier: response?.SupplierName || "",
                productIdKey: response?.ProductId || "",
                rawProductId: rawId,
                productSource: source,
                CurrencyCode: response?.CurrencyCode || "",
                PricingCurrencyCode: response?.PricingCurrencyCode || "",
                asiId: rawId || "",
                privateNote: response?.ShortDescription || "",
                sku: response?.SKU || "",
                pricingRows:   keepPricing
                  ? prev.pricingRows
                  : sortPricingRowsForHydration(normalizedTransformedPricingRows),
                decorations:   keepDecorations ? prev.decorations     : decorationsMapped,
                productionDecorationLocations:
                  response?.productionDecorationLocations ||
                  response?.DecorationLocations ||
                  prev.productionDecorationLocations ||
                  [],
                productionDecorationDeriveFromUserAddresses:
                  typeof response?.productionDecorationDeriveFromUserAddresses === "boolean"
                    ? response.productionDecorationDeriveFromUserAddresses
                    : Boolean(
                        response?.DecorationDeriveFromUserAddresses ??
                          prev.productionDecorationDeriveFromUserAddresses
                      ),
                selectedTax: response?.Tax ? response.Tax?.toString() : '',
                selectedTaxRate: typeof response?.taxRate?.value === 'number' ? response.taxRate.value : null,
                // Persisted OrderItem.SupplierId is an internal Suppliers FK.
                // The ASI catalogue supplier ID is not valid for this column.
                supplierId: response?.SupplierId ?? response?.supplierId ?? null,
              };
            });
            try {
                 const { colorOptions: co, sizeOptions: so, skuOptions: skuOpts } = extractProductOptionsForEditItem(response, normalizedTransformedPricingRows, pureAsiProductData);
                 const variationOpts = extractVariationOptionsForEditItem(response, pureAsiProductData);
                 console.groupCollapsed("🎨 Product options for Edit Item (fetchOrderItemDetails)");
                 console.log("sizes", so?.slice(0,5));
                 console.log("colors", co?.slice(0,5));
                 console.log("skus", skuOpts?.slice(0,5));
                 console.log("variations", variationOpts?.slice(0,5));
                 console.groupEnd();
                 setColorOptions(co);
                 setSizeOptions(so);
                 setSkuOptions(skuOpts);
                 setVariationOptions(variationOpts);
            } catch (e) {
                 console.warn("No product options found for size/color (fetch)", e);
                 setVariationOptions([]);
            }
            setOrderItem(response)
            const customerId = order?.attachedCustomers?.[0]
            setItemLoading(false)
            if (customerId) {
                await fetchClientFiles(customerId)
            }
        } catch (error) {
            console.log(error)
            setItemLoading(false)
            console.error(`[EditProduct] Failed to get Order Item: ${error?.message}`)
            onClose()

        }
    }


    const handleSave = async () => {
        const normalizedProductResources = normalizePromoStoreProductResources(formData?.productResources);
        const productResourceValidation = validatePromoStoreProductResources(normalizedProductResources);
        if (isPromoStoreProduct && !productResourceValidation.isValid) {
            setFormData((prev) => ({ ...prev, productResources: normalizedProductResources }));
            return;
        }
        if (debugRender) {
            console.log("[EditProduct] handleSave:start", {
                orderItemId,
                isArtworkLocked,
                loading,
            });
        }
        const mappedPricingRows = assignSequentialPricingSortOrder(formData.pricingRows).map((row) => {
            const pricingId = getPersistedPricingId(row.id);
            const isInternationalCharge = INTERNATIONAL_PRICE_TYPES.includes(row.priceType);
            const isCostCharge = ["Run Charge", "Fixed Charge"].includes(row.priceType);
            const modeSource = isInternationalCharge ? row.internationalPricingOption : row.pricingOption;
            const normalizedPricingOption = normalizeDisplayStatus(modeSource);
            const displayStatus = inventoryMode
              ? normalizeInventoryTracking(row.inventoryTracking)
              : normalizedPricingOption;

            return ({
            ...(pricingId ? { id: pricingId } : {}),
            Color: row.color || "",
            Size: row.size || "",
            Quantity: row.quantity || 1,
            InventoryQuantity:
              inventoryMode && row.inventoryQuantity !== "" && row.inventoryQuantity != null
                ? Number(row.inventoryQuantity)
                : null,
            SortOrder: resolvePersistedPricingSortOrder(row.sortOrder) ?? 0,
            NetCost: row.netCost || 0,
            ConvertedNetCost: row.convertedNetCost ?? null,
            ExchangeRate: row.exchangeRate ?? null,
            Margin: row.margin || 0,
            Retail: row.retail || 0,
            ClientPrice: row.clientPrice || 0,
            Total: row.total || 0,
            SKU: row.sku || "",
            CostName: row.chargeName || "",
            Type: row.priceType === "Breakdown"
                ? "BreakDown"
                : isCostCharge
                    ? "Cost"
                    : isInternationalCharge
                        ? "International Cost"
                        : "",
            SubType: row.priceType === "Run Charge"
                ? "Run Charge"
                : isInternationalCharge
                    ? row.priceType
                    : "",
            DisplayStatus: displayStatus,
            IsLocked: !!row.isLocked,
            IsRetailLocked: !!row.isRetailLocked
        });
        });

        // call this in your handleSave instead of the old mapDecorations
const mapDecorationsForSave = (decorations = [], pricingRows = []) =>
  (decorations || []).map(dec => {
    const dd   = dec?.DecoratorDetails || {};
    const rows = Array.isArray(dec?.rows) ? dec.rows : [];
    const isElite = dd.source === 'elite';
    const totalPricingQty = (Array.isArray(pricingRows) ? pricingRows : []).reduce((sum, row) => {
      const qty = Number(row?.quantity ?? row?.Quantity ?? 0);
      return sum + (Number.isFinite(qty) ? qty : 0);
    }, 0);
    const sharedGroupKey = String(dd?.sharedGroupKey ?? dec?.sharedGroupKey ?? "").trim();
    const sharedQuantityModeRaw = String(dd?.sharedQuantityMode ?? dec?.sharedQuantityMode ?? "").trim().toLowerCase();
    const sharedQuantityMode = (sharedQuantityModeRaw === "pooled" || sharedQuantityModeRaw === "shared")
      ? "pooled"
      : "line";
    const explicitSharedQty = Number(dd?.sharedTotalQuantity ?? dec?.sharedTotalQuantity);
    const sharedTotalQuantity = Number.isFinite(explicitSharedQty) && explicitSharedQty > 0
      ? explicitSharedQty
      : (sharedGroupKey && sharedQuantityMode === "pooled" && totalPricingQty > 0 ? totalPricingQty : null);

    // Parent decoratorId (elite prefers the per-row, else dd.decoratorId)
    const decoratorId =
      isElite
        ? (rows.find(r => r?.decoratorId != null)?.decoratorId ?? dd.decoratorId ?? null)
        : (dd.decoratorId ?? null);

    // Build row bindings from UI rows

    // !!!! [ADDING-2-DECORATOR-DETAILS] - AREA 1 - IMPORT LINE TO EDIT FOR SAVING:
    const rowBindings = rows.map((r, idx) => ({
      rowIndex:    idx,
      chargeId:    r?.chargeId ?? null,
      chargeName:  r?.chargeName ?? r?.Name ?? "",
      productionInputLabel: r?.productionInputLabel ?? null,
      productionInputValue: r?.productionInputValue ?? null,
      decoratorId: r?.decoratorId ?? decoratorId ?? null,
      supplierId:  r?.supplierId ?? null,
      addressId:   r?.addressId ?? null,
      productionDepartmentId: r?.productionDepartmentId ?? null,
      quantity: savedDecorationQuantity(r),
      sharedGroupKey: String(r?.sharedGroupKey ?? sharedGroupKey ?? "").trim() || null,
      sharedQuantityMode,
      sharedTotalQuantity,
    }));

    // A parent Matrix ID may only describe a one-charge decoration or rows
    // that ALL share that ID. Never apply it to an unrelated unbound row.
    const distinct = Array.from(new Set(rowBindings.map(b => b.chargeId).filter(v => v != null)));
    const allRowsShareCharge = rowBindings.length > 0 && distinct.length === 1 &&
      rowBindings.every(b => b.chargeId != null && String(b.chargeId) === String(distinct[0]));
    const safeParentChargeId = allRowsShareCharge
      ? distinct[0]
      : (rowBindings.length <= 1 && distinct.length === 0 ? (dd.chargeId ?? null) : null);

    const ddOut = {
      ...dd,
      decoratorId,
      rowBindings,
      RowBindings: rowBindings.map(binding => ({ ...binding })),
      ...(sharedGroupKey ? { sharedGroupKey } : {}),
      ...(sharedGroupKey ? { sharedQuantityMode } : {}),
      ...(sharedGroupKey && sharedTotalQuantity != null ? { sharedTotalQuantity } : {}),
    };
    if (safeParentChargeId != null) ddOut.chargeId = safeParentChargeId;
    else delete ddOut.chargeId;     // avoid smearing on mixed sets

    const sourceArtWorks = Array.isArray(dec?.ArtWorks)
      ? dec.ArtWorks
      : Array.isArray(dec?.artWorks)
        ? dec.artWorks
        : [];
    const sourceArtWork = dec?.ArtWork || dec?.artWork || sourceArtWorks[0] || {};
    const sourceArtworkForm = dec?.artwork || {};
    const normalizeArtworkForSave = (artwork = {}) => ({
      id:                  artwork?.id ?? undefined,
      ImprintType:         artwork?.imprintType ?? artwork?.ImprintType ?? "",
      Charge:              artwork?.charge ?? artwork?.Charge ?? 0,
      ProofRequired:       artwork?.proofRequired ?? artwork?.ProofRequired ?? "",
      ProofRequiredAction: artwork?.proofRequiredAction ?? artwork?.ProofRequiredAction ?? null,
      LogoName:            artwork?.logoName ?? artwork?.LogoName ?? "",
      LogoSize:            artwork?.logoSize ?? artwork?.LogoSize ?? "",
      LogoColor:           artwork?.logoColor ?? artwork?.LogoColor ?? "",
      RepeatLogo:          artwork?.repeatLogo ?? artwork?.RepeatLogo ?? "",
      SupplierNotes:       artwork?.supplierNotes ?? artwork?.SupplierNotes ?? "",
      CustomerFileId:      artwork?.customerFileId ?? artwork?.CustomerFileId ?? null,
      Image:               artwork?.selectedImage ?? artwork?.Image ?? null,
      PreviewUrl:
        artwork?.previewUrl ??
        artwork?.PreviewUrl ??
        artwork?.previewImageUrl ??
        null,
    });
    const artworkChoices = (
      sourceArtWorks.length
        ? sourceArtWorks
        : [sourceArtWork]
    )
      .filter((artwork) => artwork && Object.keys(artwork).length > 0)
      .map(normalizeArtworkForSave);
    const currentArtwork = normalizeArtworkForSave({
      ...sourceArtWork,
      ...sourceArtworkForm,
      id: sourceArtWork?.id ?? sourceArtworkForm?.id,
    });
    const artWorksForSave = artworkChoices.length
      ? artworkChoices.map((artwork, index) => (index === 0 ? { ...artwork, ...currentArtwork, id: artwork.id } : artwork))
      : [currentArtwork];

    const decorationId = getPersistedDecorationId(dec.id);

    return {
      ...(decorationId ? { id: decorationId } : {}),
      Location: dec.Location || dec.location || "",

      DecoratorDetails: ddOut,

      // legacy path: still provide Charge[]
      // !!!! [ADDING-2-DECORATOR-DETAILS] - AREA 2 - IMPORT LINE TO EDIT FOR SAVING:
      Charge: rows.map(row => {
        // Match the Edit Item Fixed Charge UI: one charge, never one per product.
        const isFixedCharge = isFixedChargeRow(row);
        const quantity = savedDecorationQuantity(row);
        const netCost = Number(row.netCost || 0);
        const margin = Number(row.margin ?? defaultMargin);
        const retail = Number(row.retail || 0);

        const isRunCharge = !isFixedCharge && (
          row.priceType === "Run Charge" ||
          row.type === "Run Charge" ||
          row.subType === "Run Charge" ||
          row.SubType === "Run Charge" ||
          row.chargeType === "Run" ||
          row.ChargeType === "Run" ||
          row.isRunCharge === true
        );

        const isFixedZeroMarginCharge =
          !isRunCharge &&
          quantity === 1 &&
          margin === 0 &&
          retail > 0 &&
          Math.abs(retail - netCost) <= 0.01;

        const clientPrice = isFixedZeroMarginCharge
          ? retail
          : Number(row.clientPrice ?? row.retail ?? 0);

        const total = isFixedCharge
          ? +(clientPrice * 1).toFixed(2)
          : Number(row.total ?? ((retail * quantity).toFixed(2)));

        return {
          Name:          row.chargeName || row.Name || "",
          Quantity:      quantity,
          NetCost:       netCost,
          Margin:        margin,
          Retail:        retail,
          ClientPrice:   clientPrice,
          Total:         total,
          Type:          !isFixedCharge && row.priceType === "Breakdown" ? "BreakDown" : "Cost",
          SubType:       isRunCharge ? "Run Charge" : (
            isFixedCharge || ["fixed", "fixed charge"].includes(String(row.subType ?? row.SubType ?? row.chargeType ?? row.ChargeType ?? "").toLowerCase())
              ? "Fixed Charge"
              : (row.subType ?? row.SubType ?? "")
          ),
          DisplayStatus: normalizeDisplayStatus(row.pricingOption),
          IsLocked:      !!row.isLocked,

          chargeId:     row.chargeId ?? null,
          productionInputLabel: row.productionInputLabel ?? null,
          productionInputValue: row.productionInputValue ?? null,
          decoratorId:  row.decoratorId ?? decoratorId ?? null,
          supplierId:   row.supplierId ?? null,
          addressId:    row.addressId ?? null,
          CurrencyCode: row.CurrencyCode ?? row.currencyCode ?? null,
          currencyCode: row.currencyCode ?? row.CurrencyCode ?? null,
          PricingCurrencyCode: row.PricingCurrencyCode ?? row.pricingCurrencyCode ?? null,
          pricingCurrencyCode: row.pricingCurrencyCode ?? row.PricingCurrencyCode ?? null,
          ConvertedNetCost: row.ConvertedNetCost ?? row.convertedNetCost ?? null,
          convertedNetCost: row.convertedNetCost ?? row.ConvertedNetCost ?? null,
          ExchangeRate: row.ExchangeRate ?? row.exchangeRate ?? null,
          exchangeRate: row.exchangeRate ?? row.ExchangeRate ?? null,
          convertedCurrency: row.convertedCurrency ?? null,
          productionDepartmentId: row.productionDepartmentId ?? null,
          sharedGroupKey: String(row?.sharedGroupKey ?? sharedGroupKey ?? "").trim() || null,
          sharedQuantityMode,
          sharedTotalQuantity,
        };
      }),

      ArtWorks: artWorksForSave,
      ArtWork: artWorksForSave[0] || null,
    };
  });



        const firstBreakdownSku =
          (formData.pricingRows.find(r => r.priceType === "Breakdown")?.sku || "").trim();

        const itemSKU =
          (formData.SKU || "").trim() || firstBreakdownSku;

        const pricingSnapshot = computeOrderItemPricingSnapshot({
          pricingRows: formData.pricingRows,
          decorations: formData.decorations,
          selectedTaxRate: formData?.selectedTaxRate,
          selectedTax: formData?.selectedTax,
          taxOptions,
          retailMode: pricingPolicy.retailMode,
        });

        const promoStoreTaxState = isPromoStoreProduct
          ? buildPromoStoreTaxState(formData, taxOptions)
          : null;
        const selectedTaxIds = normalizeTaxIds(formData);
        const selectedTaxOption = findTaxOptionById(
          taxOptions,
          formData?.selectedTax ?? selectedTaxIds[0]
        );
        const explicitTaxPatch = selectedTaxOption
          ? {
              ...buildProductTaxPatch(selectedTaxOption),
              ...(selectedTaxIds.length > 1
                ? { taxIds: selectedTaxIds, TaxIds: selectedTaxIds }
                : {}),
            }
          : null;

        // Tax selection is authoritative. Rebuild every derived tax field from the
        // currently selected tax IDs so a prior 7%, 5%, exempt, HST, etc. breakdown
        // cannot survive a real tax change.
        const currentTaxProduct = {
          ...initialItem,
          Tax: promoStoreTaxState?.Tax ?? formData?.selectedTax,
          ...(explicitTaxPatch || {}),
          ...(selectedTaxIds.length
            ? { taxIds: selectedTaxIds, TaxIds: selectedTaxIds }
            : {}),
        };
        const resolvedCurrentTaxes = resolveProductTaxes(currentTaxProduct, { taxOptions });
        const canonicalTaxBreakdown = buildResolvedTaxBreakdown(
          resolvedCurrentTaxes,
          pricingSnapshot.subtotal
        );
        const canonicalTaxAmount = +canonicalTaxBreakdown
          .reduce((sum, tax) => sum + (Number(tax?.taxAmount) || 0), 0)
          .toFixed(2);
        const canonicalTotalInclTax = +(
          (Number(pricingSnapshot.subtotal) || 0) + canonicalTaxAmount
        ).toFixed(2);
        const canonicalPricingSnapshot = {
          ...pricingSnapshot,
          taxRate: resolvedCurrentTaxes.reduce(
            (sum, tax) => sum + (Number(tax?.rate) || 0),
            0
          ),
          taxAmount: canonicalTaxAmount,
          total: canonicalTotalInclTax,
        };
        const normalizedPromoStoreImages = normalizePromoStoreImageSelection({
          selectedImage: formData.selectedImage,
          imageUrls: formData.imageUrls,
        });
        const normalizedSelectedCategories = normalizePromoStoreCategorySelections(
          {
            categoryIds: formData.categoryIds,
            categories: formData.categories,
          },
          promoStoreCategories,
        );

        const mappedOrderItem = {
            id: orderItemId,
            //ProductId: formData.asiId,
            Name: formData.productName,
            ProductId: formData.productIdKey,
            RawProductId: formData.rawProductId,
            SKU: itemSKU,
            Source: formData.productSource,
            CurrencyCode: formData.CurrencyCode || initialItem?.CurrencyCode || null,
            PricingCurrencyCode: formData.PricingCurrencyCode || currencySettings.defaultCurrencyCode || formData.CurrencyCode || "CAD",
            Supplier: formData.supplier,
            SupplierId: formData.supplierId,
            ImageUrl: isPromoStoreProduct
              ? normalizedPromoStoreImages.selectedImage
              : formData.selectedImage,
            ...(isPromoStoreProduct
              ? { ImageUrls: normalizedPromoStoreImages.imageUrls }
              : {}),
            Description: isPromoStoreProduct
              ? sanitizePromoStoreDescriptionHtml(formData.description)
              : formData.description,
            ShortDescription: formData.privateNote,
            ...(isPromoStoreProduct
              ? {
                  categoryIds: normalizedSelectedCategories.categoryIds,
                  categories: normalizedSelectedCategories.categories,
                }
              : {}),
            ...(isPromoStoreProduct
              ? {
                  StorefrontPricingRules: normalizeStorefrontPricingRules(
                    formData.storefrontPricingRules
                  ),
                  Resources: normalizedProductResources
                    .filter((resource) => resource.name || resource.url)
                    .map((resource, index) => ({
                      ...(resource.id ? { id: resource.id } : {}),
                      Name: normalizePromoStoreProductResourceName(resource.name),
                      Url: normalizePromoStoreProductResourceUrl(resource.url),
                      SortOrder: index,
                    })),
                }
              : {}),
            Quantity: formData.pricingRows.reduce((sum, row) => sum + (row.quantity || 0), 0),
            Subtotal: pricingSnapshot.subtotal,
            subtotal: pricingSnapshot.subtotal,
            TaxAmount: canonicalTaxAmount,
            taxAmount: canonicalTaxAmount,
            taxableAmount: pricingSnapshot.subtotal,
            taxBreakdown: canonicalTaxBreakdown,
            Total: canonicalTotalInclTax,
            total: canonicalTotalInclTax,
            totalInclTax: canonicalTotalInclTax,
            MarginAmount: pricingSnapshot.marginAmount,
            MarginPercentage: pricingSnapshot.marginPercentage,
            TrueCost: pricingSnapshot.trueCost,
            PricingSnapshot: canonicalPricingSnapshot,
            Pricings: mappedPricingRows,
            Decorations: mapDecorationsForSave(formData.decorations, formData.pricingRows),
            Tax: promoStoreTaxState?.Tax ?? formData?.selectedTax,
            ...(explicitTaxPatch || {}),
            productionDecorationLocations: deriveDecorationLocations(formData.decorations),
            productionDecorationDeriveFromUserAddresses: Boolean(
              formData.productionDecorationDeriveFromUserAddresses
            ),
            isPromoStore: Boolean(isPromoStore),
        };


        //   console.log("charged                order item: !#!@*!@(#@!&#!@&(#!@#!@#!@&#!@&#!@&#!@#&!@#&!@", dec.rows)


        console.log("Mapped order item: !#!@*!@(#@!&#!@&(#!@#!@#!@&#!@&#!@&#!@#&!@#&!@")
        console.log(JSON.stringify(mappedOrderItem.Decorations, null, 2));


        const updatedItems = order?.Items?.map((item) => {
            if (item.id === orderItemId) {
                // Merge the updated formData into the existing item
                return { ...item, ...mappedOrderItem };
            }
            return item;
        });
        /*const payload = {
            order_id: order?.OrderId,
            customer_name: order?.CustomerName,
            email: order?.Email,
            phone: order?.Phone,
            items: updatedItems,
            total: order?.Total,
            assigned_to: order?.AssignedTo,
            order_status: order?.OrderStatus
        }*/

        console.log("[EditProduct save]", { controlledMode, source, orderItemId, initialItemId: initialItem?.id, cartLineId: initialItem?.cartLineId, });

        if (controlledMode) {
            try {
                setLoading(true);
                if (typeof onSaveProduct === "function") {
                    await onSaveProduct({
                        ...initialItem,
                        ...mappedOrderItem,
                        id: initialItem?.id ?? mappedOrderItem?.id,
                        cartLineId: initialItem?.cartLineId,
                        persistedOrderItemId: initialItem?.persistedOrderItemId ?? mappedOrderItem?.id ?? null,
                        sourceOrderItemId: initialItem?.sourceOrderItemId ?? initialItem?.originalOrderItemId ?? initialItem?.persistedOrderItemId ?? mappedOrderItem?.id ?? null,
                        originalOrderItemId: initialItem?.originalOrderItemId,
                        pricings: mappedOrderItem?.Pricings,
                        decorations: mappedOrderItem?.Decorations,
                        ...(explicitTaxPatch || {}),
                        Source: mappedOrderItem?.Source ?? initialItem?.Source,
                        cartSource: source,
                        categoryIds: mappedOrderItem?.categoryIds ?? [],
                        categories: mappedOrderItem?.categories ?? [],
                      });
                }
                setLoading(false);
                onClose();
            } catch (error) {
                setLoading(false);
                const responseData = error?.response?.data || {};
                const quantityLine = Array.isArray(responseData?.lines) ? responseData.lines[0] : null;
                const message = responseData?.code === 'INVOICE_QUANTITY_EXCEEDS_REMAINING' && quantityLine
                    ? `Quantity ${quantityLine.requested} exceeds the remaining source quantity of ${quantityLine.remaining}.`
                    : responseData?.message || 'Unable to save this line item.';
                toast.error(message);
                console.error('[EditProduct] Failed to save controlled product', {
                    error,
                    response: responseData,
                });
            }
            return;
        }

        try {
            setLoading(true)
            onSaveLifecycleChange?.(true);
            const saveStartedAt = performance.now();
            if (debugRender) {
                console.log("[EditProduct] updateOrderItems:request", { orderItemId: mappedOrderItem?.id });
            }
            const response = await updateOrderItems(mappedOrderItem?.id, mappedOrderItem); // Replace with your actual API call
            if (debugRender) {
                console.log("[EditProduct] updateOrderItems:success", {
                    orderItemId: mappedOrderItem?.id,
                    responseSuccess: response?.data?.success,
                });
            }
            const saveMs = Math.round(performance.now() - saveStartedAt);
            console.info("[Order Cart timing] Product persisted", { orderItemId, saveMs });
            console.info('[EditProduct] Updated order item successfully')
            if (typeof fetchOrder === "function") {
                if (debugRender) {
                    console.log("[EditProduct] fetchOrder:invoke", { suppressLoader: true });
                }
                const refreshStartedAt = performance.now();
                await fetchOrder({ suppressLoader: true });
                console.info("[Order Cart timing] Cart refreshed", { orderItemId, refreshMs: Math.round(performance.now() - refreshStartedAt), totalMs: Math.round(performance.now() - saveStartedAt) });
            }
            onClose();
        } catch (error) {
            setLoading(false)
            if (debugRender) {
                console.error("[EditProduct] handleSave:error", error);
            }
            console.error('[EditProduct] Failed to update order item', error)
            toast.error(error?.response?.data?.message || 'Unable to save the product. Please retry.');
        } finally {
            onSaveLifecycleChange?.(false);
            setLoading(false);
        }
    };

  useEffect(() => {
    fetchTermsAndTaxes();
  }, [])

  useEffect(() => {
    if (!open) return;
    setCurrencySettingsLoaded(false);
    const instanceUniqueId = resolveInstanceUniqueId();
    if (!instanceUniqueId) {
      setDefaultMargin(DEFAULT_MARGIN_FALLBACK);
      setCurrencySettings(DEFAULT_CURRENCY_SETTINGS);
      setCurrencySettingsLoaded(true);
      return;
    }
    let mounted = true;
    const loadDefaults = async () => {
      try {
        const instanceRes = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byinstanceUniqueId/${instanceUniqueId}`
        );
        const instanceId = instanceRes?.data?.id;
        if (!instanceId) {
          if (mounted) {
            setDefaultMargin(DEFAULT_MARGIN_FALLBACK);
            setCurrencySettings(DEFAULT_CURRENCY_SETTINGS);
            setCurrencySettingsLoaded(true);
          }
          return;
        }
        const defaultsRes = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${instanceId}/defaults`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );
        const defaults = defaultsRes?.data?.defaults || {};
        const rawMargin = defaults.defaultMargin;
        const parsed = Number(rawMargin);
        if (mounted) {
          setDefaultMargin(Number.isFinite(parsed) ? parsed : DEFAULT_MARGIN_FALLBACK);
          setCurrencySettings(normalizeCurrencySettings(defaults.currencySettings));
          setCurrencySettingsLoaded(true);
        }
      } catch (error) {
        if (mounted) {
          setDefaultMargin(DEFAULT_MARGIN_FALLBACK);
          setCurrencySettings(DEFAULT_CURRENCY_SETTINGS);
          setCurrencySettingsLoaded(true);
        }
      }
    };
    loadDefaults();
    return () => {
      mounted = false;
    };
  }, [open, order?.InstanceUniqueIds, order?.instanceUniqueIds, order?.instanceUniqueId, order?.InstanceUniqueId]);

  useEffect(() => {
    if (!open || !currencySettingsLoaded) return;
    setFormData((prev) => {
      if (prev?.PricingCurrencyCode) return prev;
      return {
        ...prev,
        PricingCurrencyCode: resolvePricingCurrencyCode(
          { PricingCurrencyCode: initialItem?.PricingCurrencyCode, CurrencyCode: prev?.CurrencyCode || initialItem?.CurrencyCode },
          currencySettings
        ),
      };
    });
  }, [open, currencySettingsLoaded, currencySettings, initialItem?.PricingCurrencyCode, initialItem?.CurrencyCode]);

  useEffect(() => {
    if (!open) return;
    if (touchedRef.current.pricingRows) return;
    if (Array.isArray(initialItem?.pricings) && initialItem.pricings.length > 0) return;
    if (defaultMargin === DEFAULT_MARGIN_FALLBACK) return;

    setFormData((prev) => {
      const rows = Array.isArray(prev.pricingRows) ? prev.pricingRows : [];
      if (!rows.length) return prev;
      const shouldUpdate = rows.every((row) =>
        Number(row.margin ?? DEFAULT_MARGIN_FALLBACK) === DEFAULT_MARGIN_FALLBACK
      );
      if (!shouldUpdate) return prev;
      return {
        ...prev,
        pricingRows: rows.map((row) => ({
          ...row,
          margin: defaultMargin,
        })),
      };
    });
  }, [defaultMargin, open, initialItem?.pricings]);

  const allRetailRowsLocked = Array.isArray(formData?.pricingRows) &&
    formData.pricingRows.length > 0 &&
    formData.pricingRows.every((row) => isLockedBool(row?.isRetailLocked));

  const toggleAllRetailLocks = () => {
    markTouched('pricingRows');
    setFormData((prev) => {
      const pricingRows = Array.isArray(prev?.pricingRows) ? prev.pricingRows : [];
      if (!pricingRows.length) return prev;
      return { ...prev, pricingRows: toggleRetailPricingRowLocks(pricingRows) };
    });
  };

  const allNetCostRowsLocked = (() => {
    const pricingRows = Array.isArray(formData?.pricingRows) ? formData.pricingRows : [];
    const decorationRows = (Array.isArray(formData?.decorations) ? formData.decorations : [])
      .flatMap((decoration) => Array.isArray(decoration?.rows) ? decoration.rows : []);
    const lockableCount = pricingRows.length + decorationRows.length;

    if (!lockableCount) return false;

    return (
      pricingRows.every((row) => isLockedBool(row?.isLocked)) &&
      decorationRows.every((row) => isLockedBool(row?.isLocked))
    );
  })();

  const toggleAllNetCostLocks = () => {
    markTouched('pricingRows');
    markTouched('decorations');
    setFormData((prev) => {
      const pricingRows = Array.isArray(prev?.pricingRows) ? prev.pricingRows : [];
      const decorations = Array.isArray(prev?.decorations) ? prev.decorations : [];
      const decorationRows = decorations.flatMap((decoration) => Array.isArray(decoration?.rows) ? decoration.rows : []);
      const lockableCount = pricingRows.length + decorationRows.length;
      const allLocked = lockableCount > 0 &&
        pricingRows.every((row) => isLockedBool(row?.isLocked)) &&
        decorationRows.every((row) => isLockedBool(row?.isLocked));
      const nextLocked = !allLocked;

      return {
        ...prev,
        pricingRows: pricingRows.map((row) => ({
          ...row,
          isLocked: nextLocked,
        })),
        decorations: decorations.map((decoration) => ({
          ...decoration,
          rows: Array.isArray(decoration?.rows)
            ? decoration.rows.map((row) => ({
              ...row,
              isLocked: nextLocked,
            }))
            : decoration?.rows,
        })),
      };
    });
  };

  // toggle lock for a row
  const toggleRowLock = (index) => {
    setPricingRowsSafe(prev => {
      const rows = [...prev];
      rows[index] = { ...rows[index], isLocked: !isLockedBool(rows[index]?.isLocked) };
      return rows;
    });
  };

  // generic per-row updater (index + patch)
  const patchPricingRow = (index, patch) => {
    setPricingRowsSafe(prev => {
      const rows = [...prev];
      const nextRow = applyRowPatch(
        rows[index],
        patch,
        defaultMargin,
        pricingPolicy,
        respectPricingLocks
      );
      const isPromoStoreRetailOnlyEdit =
        usesIndependentRetail(pricingPolicy) &&
        patchHasOwn(patch, "retail") &&
        !patchHasOwn(patch, "margin") &&
        !patchHasOwn(patch, "netCost");
      const isPromoStoreQuantityOnlyEdit =
        usesIndependentRetail(pricingPolicy) &&
        patchHasOwn(patch, "quantity") &&
        !patchHasOwn(patch, "retail") &&
        !patchHasOwn(patch, "margin") &&
        !patchHasOwn(patch, "netCost");

      rows[index] = isPromoStoreRetailOnlyEdit
        ? { ...nextRow, __pricingEditIntent: PRICING_EDIT_INTENT.RETAIL_ONLY }
        : isPromoStoreQuantityOnlyEdit
          ? { ...nextRow, __pricingEditIntent: PRICING_EDIT_INTENT.QUANTITY_ONLY }
          : nextRow;
      return rows;
    });
  };

    /* START - EXPERIMENTAL CODE TO SPEED UP EDIT FUNCTION - 2025 - 07 - 24 */

    /* useEffect(() => {
        if (orderItemId) {
            fetchOrderItemDetails(orderItemId);
        }
    }, [orderItemId]) */

    useEffect(() => {
      // 1) only run if the dialog is open
      if (!open) return;

      if (shouldFetchOrderItemForEditProduct({
        open,
        controlledMode,
        isPromoStore,
        initialItem,
        orderItemId,
        source,
      })) {
        fetchOrderItemDetails(orderItemId);
      }
    }, [open, orderItemId, initialItem, isPromoStore, controlledMode, source]);

    /* END - EXPERIMENTAL CODE TO SPEED UP EDIT FUNCTION - 2025 - 07 - 24 */

    useEffect(() => {
        if (!open) return;
        if (!order || !Array.isArray(order.Items)) return;

        // Only do once per open session
        if (decorationEntirety.bootstrapped) return;

        // Seed rows for every OTHER item with status=loading (don’t block paint)
        setDecorationEntirety(prev => {
          const next = { ...prev, byItemId: { ...prev.byItemId }, bootstrapped: true };
          order.Items.forEach(it => {
            if (!it?.id) return;
            if (String(it.id) === String(orderItemId)) return; // skip current item
            if (!next.byItemId[it.id]) {
              next.byItemId[it.id] = {
                status: 'loading',
                raw: it.decorations ?? it.Decorations ?? []
              };
            }
          });
          return next;
        });

        // Defer heavy mapping so first paint/summaries render immediately
        setTimeout(() => {
          // map in a micro-batch
          const batch = [...(order.Items || [])].filter(it => String(it.id) !== String(orderItemId));
          batch.forEach(it => {
            const defaultLoc = it?.Imprinting?.Locations?.Values?.[0]?.Name || '';
            try {
              const mapped = mapDecorationsSafe(
                it?.decorations ?? it?.Decorations ?? [],
                defaultLoc,
                sourceOptions
              );
              setDecorationEntirety(prev => ({
                ...prev,
                byItemId: {
                  ...prev.byItemId,
                  [it.id]: { status: 'ready', raw: it?.decorations ?? it?.Decorations ?? [], mapped }
                }
              }));
            } catch (e) {
              setDecorationEntirety(prev => ({
                ...prev,
                byItemId: {
                  ...prev.byItemId,
                  [it.id]: { status: 'error', raw: it?.decorations ?? it?.Decorations ?? [], mapped: [], error: e }
                }
              }));
            }
          });
        }, 0);
      }, [open, order, orderItemId, sourceOptions, decorationEntirety.bootstrapped]);



const toArr = (v) => Array.isArray(v) ? v : (typeof v === "string" ? (()=>{ try{const p=JSON.parse(v); return Array.isArray(p)?p:[];}catch{return []} })() : []);

const calculatePricingSummary = (formData, taxOptions=[]) => {
  const snapshot = computeOrderItemPricingSnapshot({
    pricingRows: toArr(formData?.pricingRows),
    decorations: toArr(formData?.decorations),
    selectedTaxRate: formData?.selectedTaxRate,
    selectedTax: formData?.selectedTax,
    taxOptions,
    retailMode: pricingPolicy.retailMode,
  });

  return {
    subtotal: snapshot.subtotal,
    marginAmount: snapshot.marginAmount,
    marginPercentage: snapshot.marginPercentage,
    decorationCharges: 0,
    taxAmount: snapshot.taxAmount,
    finalTotal: snapshot.total,
  };
};


    const summary = calculatePricingSummary(formData, taxOptions);
    const handleDone = async (customerId, formdata) => {
        try {
            const res = await uploadFile(customerId, formdata);
            await fetchClientFiles(customerId)
            return res
        } catch (error) {
            console.error("Upload failed:", error);
        }

    };

    const previewAssessmentEntries = useMemo(() => {
      const normalizedCustomerFiles = (Array.isArray(clientFiles) ? clientFiles : [])
        .map((file) => normalizeCustomerFileEntry(file));
      const customerFilesById = new Map(
        normalizedCustomerFiles
          .filter((file) => file.customerFileId)
          .map((file) => [String(file.customerFileId), file])
      );
      const customerFilesByOriginalUrl = new Map(
        normalizedCustomerFiles
          .map((file) => [normalizeArtworkOriginalUrl(file.originalUrl || file.filePath || ""), file])
          .filter(([url]) => Boolean(url))
      );

      const entries = [...normalizedCustomerFiles];
      const decorations = Array.isArray(formData?.decorations) ? formData.decorations : [];

      decorations.forEach((decoration) => {
        if (decoration?.artwork) {
          entries.push(
            resolveArtworkCustomerFile({
              artwork: decoration.artwork,
              customerFilesById,
              customerFilesByOriginalUrl,
            })
          );
        }
        if (Array.isArray(decoration?.rows)) {
          decoration.rows.forEach((row) => {
            if (row?.artwork) {
              entries.push(
                resolveArtworkCustomerFile({
                  artwork: row.artwork,
                  customerFilesById,
                  customerFilesByOriginalUrl,
                })
              );
            }
          });
        }
      });

      return entries;
    }, [clientFiles, formData?.decorations]);

    const handleEnsuredCustomerFilePreviews = useCallback((files = []) => {
      if (!Array.isArray(files) || !files.length) return;

      const normalizedFiles = files.map((file) => normalizeCustomerFileEntry(file));
      const filesById = new Map(
        normalizedFiles
          .filter((file) => file.customerFileId)
          .map((file) => [String(file.customerFileId), file])
      );
      const filesByOriginalUrl = new Map(
        normalizedFiles
          .map((file) => [normalizeArtworkOriginalUrl(file.originalUrl || file.filePath || ""), file])
          .filter(([url]) => Boolean(url))
      );

      if (!filesById.size && !filesByOriginalUrl.size) return;

      setClientFiles((prev) => {
        let changed = false;
        const next = (Array.isArray(prev) ? prev : []).map((file) => {
          const normalized = normalizeCustomerFileEntry(file);
          const ensured =
            filesById.get(String(normalized.customerFileId || "")) ||
            filesByOriginalUrl.get(
              normalizeArtworkOriginalUrl(normalized.originalUrl || normalized.filePath || "")
            );
          if (!ensured) return file;
          changed = true;
          return {
            ...file,
            previewImageUrl: ensured.previewImageUrl || file.previewImageUrl || null,
            previewUrl: ensured.previewImageUrl || file.previewUrl || null,
            previewStatus: ensured.previewStatus ?? file.previewStatus ?? null,
            previewGeneratedAt:
              ensured.previewGeneratedAt ?? file.previewGeneratedAt ?? null,
            previewError: ensured.previewError ?? file.previewError ?? null,
            customerFileId:
              ensured.customerFileId ??
              file.customerFileId ??
              file.CustomerFileId ??
              null,
            CustomerFileId:
              ensured.customerFileId ??
              file.CustomerFileId ??
              file.customerFileId ??
              null,
          };
        });
        return changed ? next : prev;
      });

      setFormData((prev) => {
        const currentDecorations = Array.isArray(prev?.decorations) ? prev.decorations : [];
        let changed = false;

        const mergeArtwork = (artwork = {}) => {
          const normalizedArtwork = normalizeArtworkFileEntry(artwork);
          const ensured =
            filesById.get(String(getArtworkCustomerFileId(normalizedArtwork) || "")) ||
            filesByOriginalUrl.get(
              normalizeArtworkOriginalUrl(
                normalizedArtwork.originalUrl || normalizedArtwork.filePath || ""
              )
            );
          if (!ensured) return artwork;

          const nextPreviewUrl = getArtworkPreviewUrl(ensured) || artwork.previewUrl || null;
          const nextPreviewStatus = ensured.previewStatus ?? artwork.previewStatus ?? null;
          const nextPreviewError = ensured.previewError ?? artwork.previewError ?? null;
          const nextPreviewGeneratedAt =
            ensured.previewGeneratedAt ?? artwork.previewGeneratedAt ?? null;
          const nextCustomerFileId =
            ensured.customerFileId ??
            getArtworkCustomerFileId(artwork) ??
            null;

          if (
            nextPreviewUrl === (artwork.previewUrl || null) &&
            nextPreviewStatus === (artwork.previewStatus ?? null) &&
            nextPreviewError === (artwork.previewError ?? null) &&
            nextPreviewGeneratedAt === (artwork.previewGeneratedAt ?? null) &&
            nextCustomerFileId === (getArtworkCustomerFileId(artwork) ?? null)
          ) {
            return artwork;
          }

          changed = true;
          return {
            ...artwork,
            previewUrl: nextPreviewUrl,
            previewImageUrl: nextPreviewUrl,
            previewStatus: nextPreviewStatus,
            previewError: nextPreviewError,
            previewGeneratedAt: nextPreviewGeneratedAt,
            customerFileId: nextCustomerFileId,
            CustomerFileId: nextCustomerFileId,
          };
        };

        const nextDecorations = currentDecorations.map((decoration) => ({
          ...decoration,
          artwork: mergeArtwork(decoration?.artwork || {}),
          rows: Array.isArray(decoration?.rows)
            ? decoration.rows.map((row) => ({
                ...row,
                artwork: mergeArtwork(row?.artwork || {}),
              }))
            : decoration?.rows,
        }));

        return changed ? { ...prev, decorations: nextDecorations } : prev;
      });
    }, []);

    useEffect(() => {
      const normalizedCustomerFiles = (Array.isArray(clientFiles) ? clientFiles : [])
        .map((file) => normalizeCustomerFileEntry(file));
      if (!normalizedCustomerFiles.length) return;

      const customerFilesById = new Map(
        normalizedCustomerFiles
          .filter((file) => file.customerFileId)
          .map((file) => [String(file.customerFileId), file])
      );
      const customerFilesByOriginalUrl = new Map(
        normalizedCustomerFiles
          .map((file) => [normalizeArtworkOriginalUrl(file.originalUrl || file.filePath || ""), file])
          .filter(([url]) => Boolean(url))
      );

      setFormData((prev) => {
        const currentDecorations = Array.isArray(prev?.decorations) ? prev.decorations : [];
        let changed = false;

        const mergeArtworkFromCustomerFiles = (artwork = {}) => {
          const resolved = resolveArtworkCustomerFile({
            artwork,
            customerFilesById,
            customerFilesByOriginalUrl,
          });

          if (
            resolved.customerFileId === (getArtworkCustomerFileId(artwork) ?? null) &&
            (resolved.previewUrl || null) === (artwork.previewUrl || null) &&
            (resolved.previewImageUrl || null) === (artwork.previewImageUrl || null) &&
            (resolved.previewStatus || "") === (artwork.previewStatus || "") &&
            (resolved.previewError || "") === (artwork.previewError || "") &&
            (resolved.previewGeneratedAt || "") === (artwork.previewGeneratedAt || "")
          ) {
            return artwork;
          }

          changed = true;
          return {
            ...artwork,
            customerFileId: resolved.customerFileId ?? null,
            CustomerFileId: resolved.customerFileId ?? null,
            previewUrl: resolved.previewUrl || null,
            previewImageUrl: resolved.previewImageUrl || resolved.previewUrl || null,
            previewStatus: resolved.previewStatus || "",
            previewError: resolved.previewError || "",
            previewGeneratedAt: resolved.previewGeneratedAt || "",
          };
        };

        const nextDecorations = currentDecorations.map((decoration) => ({
          ...decoration,
          artwork: mergeArtworkFromCustomerFiles(decoration?.artwork || {}),
          rows: Array.isArray(decoration?.rows)
            ? decoration.rows.map((row) => ({
                ...row,
                artwork: mergeArtworkFromCustomerFiles(row?.artwork || {}),
              }))
            : decoration?.rows,
        }));

        return changed ? { ...prev, decorations: nextDecorations } : prev;
      });
    }, [clientFiles]);

    // near your other `useState` hooks:
const [locationOptions, setLocationOptions] = useState([]);
const [imprintSizeOptions, setImprintSizeOptions] = useState([]);
const [imprintColorOptions, setImprintColorOptions] = useState([]);

// after your `useEffect` that seeds `decorationSource`:
useEffect(() => {
    // sizes & colors come straight off the ASI blob
    const sizeVals  = asiLiveRawData?.Imprinting?.Sizes?.Values  || [];
    const colorVals = asiLiveRawData?.Imprinting?.Colors?.Values || [];

    setImprintSizeOptions(
      sizeVals.map(s => ({ label: s.Name, value: s.Name }))
    );
    setImprintColorOptions(
      colorVals.map(c => ({ label: c.Name, value: c.Name }))
    );

    // only supplier methods have custom location lists
    if (decorationSource?.type !== 'supplier') {
      setLocationOptions([]);
      return;
    }

    // find the chosen method, pluck its “Imprint Location” bucket
    const method    = imprintMethods.find(m => m.Code === decorationSource.methodCode);
    const locOption = method?.Options?.find(o => o.Type === 'Imprint Location');
    const rawLocs   = locOption?.Values || [];

    setLocationOptions(
      rawLocs.map(item =>
        typeof item === 'string'
          ? { label: item, value: item }
          : { label: item.Name, value: item.Name }
      )
    );
  }, [asiLiveRawData, decorationSource, imprintMethods]);


// Find the actual ASI method object and use its Name
const decorationSourceMethod = uniqueImprintMethods.find(
    m => `supplier|${m.Code}` === decorationSource?.value
  );
  const decorationSourceLabel = decorationSourceMethod?.Name || "";

  const looksCustomFromKey =
  String(formData.productIdKey || "").startsWith(CUSTOM_PREFIX);

const normalizedProductSource = String(formData.productSource || "").trim().toLowerCase();
const isCustom =
  normalizedProductSource === "custom" || looksCustomFromKey;
const isPromoWorld = normalizedProductSource === "promoworld";

const productIdUiLabel = isCustom ? "Custom ID" : isPromoWorld ? "PromoWorld ID" : "ASI ID";
const productIdUiValue = isCustom
  ? `${CUSTOM_PREFIX}${formData.rawProductId || ""}`
  : (formData.rawProductId || "");

  const productIdUi = useMemo(() => {
    // Prefer live formData
    const fdSource = formData?.productSource ?? null;
    const fdRawId  = formData?.rawProductId ?? null;
    const fdKey    = formData?.productIdKey ?? null;

    // First render fallbacks
    const parsed   = parseOrderProductId(initialItem?.ProductId || "");
    const iiSource = initialItem?.Source ?? parsed.source;
    const iiRawId  = initialItem?.RawProductId ?? parsed.id;
    const iiKey    = initialItem?.ProductId || "";

    const source        = fdSource ?? iiSource;       // 'custom' | 'asi'
    const rawId         = fdRawId  ?? iiRawId;
    const productIdKey  = fdKey    ?? iiKey;

    const looksCustom =
      (source === "custom") ||
      String(productIdKey).startsWith(CUSTOM_PREFIX) ||
      String(initialItem?.ProductId || "").startsWith(CUSTOM_PREFIX); // hard fallback

    const normalizedSource = String(source || "").trim().toLowerCase();
    return {
      label: looksCustom ? "Custom ID" : normalizedSource === "promoworld" ? "PromoWorld ID" : "ASI ID",
      value: looksCustom ? `${CUSTOM_PREFIX}${rawId || ""}` : (rawId || "")
    };
  }, [
    formData.productSource,
    formData.rawProductId,
    formData.productIdKey,
    initialItem?.Source,
    initialItem?.RawProductId,
    initialItem?.ProductId
  ]);

  const supplierCurrencyCode = resolveSupplierCurrencyCode({ asiLiveRawData, orderItem });

  const resolveEditItemCustomerId = () => {
    const raw =
      order?.attachedCustomers?.[0] ??
      order?.CustomerId ??
      order?.customerId ??
      initialItem?.CustomerId ??
      initialItem?.customerId ??
      initialItem?.customer?.id ??
      initialItem?.Customer?.id ??
      null;

    if (raw && typeof raw === "object") {
      return raw.id ?? raw.CustomerId ?? raw.customerId ?? null;
    }

    return raw ?? null;
  };

    const editItemCustomerId = resolveEditItemCustomerId();

    useEnsureCustomerFilePreviews({
      customerId: editItemCustomerId,
      entries: previewAssessmentEntries,
      enabled: open && Boolean(editItemCustomerId),
      onFilesEnsured: handleEnsuredCustomerFilePreviews,
    });

    const handleDialogKeyDown = (event) => {
        if (!shouldTriggerDialogSaveOnEnter(event)) return;
        if (loading || isArtworkLocked) return;

        event.preventDefault();
        handleSave();
    };

    useEffect(() => {
        if (!open) return;
        if (!isPromoStoreCart) return;
        if (!editItemCustomerId) return;

        fetchClientFiles(editItemCustomerId);
    }, [open, isPromoStoreCart, editItemCustomerId]);

    return (
        <>
            <Dialog open={open} onClose={onClose}
                onKeyDown={handleDialogKeyDown}
                sx={{
                    "& .MuiDialog-paper": {
                        width: "100%", height: "100%",
                        maxWidth: '100%',
                        maxHeight: '100%',
                        margin: 0
                    },
                }}
            >
                <DialogTitle className="create-order-dialog-title">
                    Edit Item
                </DialogTitle>
                <DialogContent className="create-order-dialog-content">
                    {itemloading ? <Box sx={{
                        height: '100%', width: '100%',
                    }}><LoadingComponent /> </Box> : <>
                        <form>
                            {

                            //JSON.stringify(orderItem)

                            }
                            <ItemDetails
                                orderItem={orderItem}
                                asiLiveRawData={asiLiveRawData}
                                formData={formData}
                                onChange={handleFieldChangeSafe}
                                clientFiles={clientFiles}
                                handleDone={handleDone}
                                customerId={editItemCustomerId}
                                productIdUiLabel={productIdUi.label}
                                productIdUiValue={productIdUi.value}  // "CUSTOM-123" or "553309697"
                                isPromoStore={Boolean(isPromoStore)}
                                promoStoreCategories={promoStoreCategories}
                                selectedCategoryIds={formData.categoryIds || []}
                                onChangePromoStoreCategories={(nextCategories) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    categoryIds: nextCategories.map((category) => Number(category.id)).filter(Number.isFinite),
                                    categories: nextCategories,
                                  }))
                                }
                                productResourceErrors={validatePromoStoreProductResources(formData?.productResources || []).errorsByKey}
                                onAddProductResource={() =>
                                  setProductResourcesSafe((prev) => (
                                    (prev || []).length >= MAX_PROMOSTORE_PRODUCT_RESOURCES
                                      ? prev
                                      : [
                                          ...(prev || []),
                                          createPromoStoreProductResource({
                                            sortOrder: (prev || []).length,
                                          }),
                                        ]
                                  ))
                                }
                                onRemoveProductResource={(resourceKey) =>
                                  setProductResourcesSafe((prev) => (
                                    (prev || []).filter((resource) => String(resource.id || resource.tempResourceId) !== String(resourceKey))
                                  ))
                                }
                                onChangeProductResource={(resourceKey, field, value) =>
                                  setProductResourcesSafe((prev) => (
                                    (prev || []).map((resource) => (
                                      String(resource.id || resource.tempResourceId) === String(resourceKey)
                                        ? { ...resource, [field]: value }
                                        : resource
                                    ))
                                  ))
                                }
                            />
                        </form>
                        {isPromoStoreProduct && (
                          <PromoStoreStorefrontPricingRules
                            value={formData.storefrontPricingRules}
                            onChange={(nextRules) =>
                              setFormData((prev) => {
                                const normalizedRules = normalizeStorefrontPricingRules(nextRules);
                                const wasSeparated = Boolean(
                                  prev?.storefrontPricingRules?.separateInventoryFromPricing
                                );
                                const isNowSeparated = Boolean(
                                  normalizedRules?.separateInventoryFromPricing
                                );
                                const shouldMigrateLegacyStock = !wasSeparated && isNowSeparated;

                                return {
                                  ...prev,
                                  storefrontPricingRules: normalizedRules,
                                  pricingRows: shouldMigrateLegacyStock
                                    ? (prev.pricingRows || []).map((row) => ({
                                        ...row,
                                        inventoryQuantity:
                                          row.inventoryQuantity == null || row.inventoryQuantity === ""
                                            ? Number(row.quantity || 0)
                                            : row.inventoryQuantity,
                                      }))
                                    : prev.pricingRows,
                                };
                              })
                            }
                            colorOptions={colorOptions}
                            sizeOptions={sizeOptions}
                            imageUrls={formData.imageUrls}
                          />
                        )}

                        <PricingBreakDown
                            colorOptions={colorOptions}
                            setSizeOptions={setSizeOptions}
                            setColorOptions={setColorOptions}
                            skuOptions={skuOptions}
                            sizeOptions={sizeOptions}
                            variationOptions={variationOptions}
                            orderItem={orderItem}
                            rows={formData.pricingRows}
                            setRows={setPricingRowsSafe}
                            patchRow={patchPricingRow}
                            applyPricingPatch={(row, patch) =>
                              applyRowPatch(
                                row,
                                patch,
                                defaultMargin,
                                pricingPolicy,
                                respectPricingLocks
                              )
                            }
                            inventoryMode={inventoryMode}
                            defaultMargin={defaultMargin}
                            toggleLock={toggleRowLock}
                            showRetailBulkLock={canShowRetailBulkLock({ authState, source, isPromoStoreProduct, controlledMode })}
                            retailBulkLocked={allRetailRowsLocked}
                            onToggleRetailBulkLock={toggleAllRetailLocks}
                            showNetCostLocks={showNetCostLocks}
                            bulkNetCostLocked={showNetCostLocks ? allNetCostRowsLocked : false}
                            onToggleBulkNetCostLock={showNetCostLocks ? toggleAllNetCostLocks : null}
                            pricingGridMode={Boolean(formData?.pricingGridMode)}
                            onPricingGridModeChange={(value) =>
                              setFormData((prev) => ({ ...prev, pricingGridMode: Boolean(value) }))
                            }
                            allowManualPricing={pricingPolicy.allowManualPricing}
                            canSeeManualPricingToggle={canSeeManualPricingToggle && canUseManualPricingMode(authState)}
                            decorations={formData.decorations}
                            respectRowLocks={respectPricingLocks}
                            lockConfig={{
                                 isFieldDisabled: (row, field) => {
                                   if (!respectPricingLocks) return false;
                                   // Preserve the legacy IsLocked allowlist. Only the
                                   // separate Retail lock permits editing other fields.
                                   if (!isLockedBool(row?.isLocked) && !isPromoStoreProduct) {
                                     return isLockedBool(row?.isRetailLocked) &&
                                       (field === 'retail' || field === 'clientPrice');
                                   }
                                   if (!isLockedBool(row?.isLocked)) return false;
                                   return !(
                                     field === 'netCost' ||
                                     field === 'sku' ||
                                     (
                                       field === 'inventoryQuantity' &&
                                       formData?.storefrontPricingRules?.separateInventoryFromPricing
                                     )
                                   );
                                 }
                               }}
                            supplierCurrencyCode={supplierCurrencyCode}
                            pricingCurrencyCode={formData?.PricingCurrencyCode}
                            separateInventoryFromPricing={Boolean(
                              formData?.storefrontPricingRules?.separateInventoryFromPricing
                            )}
                        />

                        {/*<Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            Show raw decoration JSON
                        </AccordionSummary>
                        <AccordionDetails>
                            <pre style={{ whiteSpace: "pre-wrap", maxHeight: 300, overflow: "auto" }}>
                            {JSON.stringify(decorationString, null, 2)}
                            </pre>
                        </AccordionDetails>
                        </Accordion>

                        <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            Show raw location JSON
                        </AccordionSummary>
                        <AccordionDetails>
                            <pre style={{ whiteSpace: "pre-wrap", maxHeight: 300, overflow: "auto" }}>
                            {JSON.stringify(locationOptions, null, 2)}
                            </pre>
                        </AccordionDetails>
                        </Accordion>

                        <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            Show raw ASI live data
                        </AccordionSummary>
                        <AccordionDetails>
                            <pre style={{ whiteSpace: "pre-wrap", maxHeight: 300, overflow: "auto" }}>
                            {JSON.stringify(asiLiveRawData, null, 2)}
                            </pre>
                        </AccordionDetails>
                        </Accordion>

                        */}

                        <Box
                            className="edit-product-decoration-source"
                            //key={decoration.id || `dec-${decorationIndex}`}
                        >
                        <h2>
                            Decoration Source
                        </h2>
                    {
                        //JSON.stringify(decorationSource)
                    }
                    {/*

                    Magic recommender / Removed by DR - 2025-07-21

                    locationOptions.length > 0 && (
                        <Box className="edit-product-location-picker" sx={{ mb: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                            <PsychologyIcon sx={{ mr: 1, color: "action.active" }} />
                            <Typography
                                component="label"
                                htmlFor="location-suggester"
                                sx={{ fontWeight: "medium" }}
                            >
                                Recommended imprint locations:
                            </Typography>
                            </Box>

                            <select
                            id="location-suggester"
                            onChange={e => {
                                setFormData(prev => ({
                                ...prev,
                                selectedLocation: e.target.value
                                }));
                            }}
                            defaultValue=""
                            >
                            <option value="" disabled>
                                Pick a supplier location...
                            </option>
                            {locationOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                {opt.label}
                                </option>
                            ))}
                            </select>
                        </Box>
                        )

                        */}


                        </Box>

                        <div className="decoration-container">
                            {decorationSource?.methodCode !== '$UNI' && (
                                <Decoration
                                    orderItem={orderItem}
                                    currentCartProduct={initialItem || orderItem}
                                    order={order}
                                    customerId={order?.attachedCustomers?.[0]}
                                    setRows={setDecorationsSafe}
                                    rows={formData.decorations}
                                    clientFiles={clientFiles}
                                    handleDone={handleDone}
                                    decorationSource={decorationSourceLabel}
                                    imprintLocations={locationOptions}
                                    imprintSizes={imprintSizeOptions}
                                    imprintColors={imprintColorOptions}
                                    /* NEW */
                                    sourceOptions={sourceOptions}
                                    imprintMethods={uniqueImprintMethods}
                                    asiLiveRawData={asiLiveRawData}
                                    mapDecorationsFromApi={mapDecorationsFromApi}
                                    decorationEntirety={decorationEntirety}
                                    pricingRows={formData.pricingRows}
                                    artworkLocks={artworkLocks}
                                    defaultMargin={defaultMargin}
                                    showNetCostLocks={showNetCostLocks}
                                    isPromoStore={Boolean(isPromoStore)}
                                    supportsPromoStoreArtworkChoices={supportsPromoStoreArtworkChoices}
                                    cartProducts={cartProducts}
                                    onCopyDecorationToProduct={onCopyDecorationToProduct}


                                />
                            )}
                        </div>

                        <PricingSummary
                            subtotal={100}
                            formData={formData}
                            setFormData={setFormData}
                            subtotal={summary.subtotal}
                            marginPercentage={summary.marginPercentage}
                            //artworkAmount={summary.artWorkAmount} Artwork removed for MVP - 2025-07-22 - DR
                            marginAmount={summary.marginAmount}
                            taxAmount={summary.taxAmount}
                            total={summary.finalTotal}
                            taxOptions={taxOptions}
                            decorationCharges={summary.decorationCharges}
                            currencySettings={currencySettings}
                        />
                    </>
                    }
                </DialogContent>
                <DialogActions className="edit-product-dialog-action-bar">
                    <Button onClick={onClose} color="error" sx={{
                        width: '100px',
                        height: '50px'
                    }}>
                        Cancel
                    </Button>


                     <Button
   onClick={handleSave}
   color="primary"
   disabled={loading || isArtworkLocked}
   sx={{ width: '120px', height: '50px' }}
 >
   {(loading || isArtworkLocked)
     ? (isArtworkLocked ? 'Preparing…' : 'Saving…')
     : 'Save'}
 </Button>

                </DialogActions>
            </Dialog>
        </>

    );
};

export {
  applyRowPatch,
  canShowRetailBulkLock,
  canUseManualPricingMode,
  isPricingRetailLocked,
  buildEditablePricingRow,
  calculateMarginFromSellPrice,
  createPromoStoreProductResource,
  isValidPromoStoreProductResourceUrl,
  MAX_PROMOSTORE_PRODUCT_RESOURCES,
  normalizeInventoryTracking,
  normalizePromoStoreProductResourceName,
  isTransientDecimal,
  normalizePromoStoreProductResources,
  normalizePromoStoreProductResourceUrl,
  normalizePricingRowsForEditState,
  resolvePricingPolicy,
  toggleRetailPricingRowLocks,
  recomputeConvertedCostBasisRow,
  recomputeManualRow,
  recomputePromoStoreUnlockedRow,
  recomputeUnlockedRow,
  validatePromoStoreProductResources,
};

export default EditProduct;
